/**
 * Hospital Marketplace Page
 * Customer-facing product catalog for Sterile Mate's healthcare solutions
 * Accessible from Admin Dashboard - Hospital-facing product showcase
 */

import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Search,
  Filter,
  ShoppingCart,
  Heart,
  ExternalLink,
  FileText,
  Phone,
  Mail,
  Star,
  CheckCircle2,
  Zap,
  Droplets,
  Sparkles,
  Package,
  Shield,
  Eye,
  Microscope,
  Wrench,
  Building2,
  Clock,
  Award,
  ChevronRight,
  Play,
  Download,
  FlaskConical,
  Truck,
  BadgeCheck,
  Leaf,
  Wind,
  ShoppingBag,
  Activity,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import SiteHeader from "@/components/SiteHeader";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";
import ProductDetailModal from "@/components/ProductDetailModal";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import {
  buildSterileProcessingProducts,
  fetchMicrocarePricing,
} from "@/data/sterileProcessingProducts";
import DMS_DATA from "@/data/directmopsales_variants.json";

// Product Categories — marketplace is focused exclusively on Janitorial & EVS
const CATEGORIES = [
  { id: "neuro-patty-sponges", name: "Neuro Patty Sponges", icon: FlaskConical },
  { id: "evs-janitorial", name: "Janitorial & EVS", icon: Sparkles },
  { id: "sterile-processing", name: "Sterile Processing", icon: FlaskConical },
  { id: "hemodialysis", name: "Hemodialysis Product & Care", icon: Droplets },
  { id: "home-health-aids", name: "Home Health Care Aids", icon: Heart },
  { id: "blood-pressure-monitors", name: "Blood Pressure Monitors", icon: Activity },
  { id: "healthcare-furniture", name: "Healthcare Examination Furniture", icon: Activity, href: "/marketplace/healthcare-examination-furniture" },
  { id: "facility-supplies", name: "Facility Building Supplies", icon: Package },
];

// Product Data
export const PRODUCTS = [
  // Neuro Patty Sponges — FABCO (Specialty / Off-The-Grid, RFQ)
  {
    id: "fabco-neuro-patties",
    name: "FABCO Neuro Patties — Latex-Free, X-Ray Detectable, Sterile",
    category: "neuro-patty-sponges",
    subcategory: "FABCO",
    brand: "FABCO",
    tagline: "Specialty OTG · 9 sizes · Request pricing",
    description: "FABCO neurosurgical patties — sterile, latex-free, X-ray detectable, wound on a count-verification card with a securely attached locating string. Sourced through Sterile Mate's specialty (Off-The-Grid, non-GPO) channel. Sold by the box (200 patties · 10 patties/card · 20 pouches/box). Nine sizes from ¼×¼ in through 1×3 in. Separate from our Authorized American Surgical Ray-Cot® line — order Fabco when your surgeons or contracts specifically require Fabco.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/fabco/neuro-patties.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/fabco/neuro-patties.jpg`,
    ],
    features: [
      "Sterile · latex-free · X-ray detectable (radiopaque)",
      "Securely attached locator string on every pattie",
      "Wound on a count-verification card",
      "10 patties per card · 1 card per pouch · 20 pouches per box (200/box)",
      "9 sizes: ¼×¼ · ½×½ · ½×1 · ½×1½ · ½×2 · ½×3 · ¾×¾ · 1×1 · 1×3",
      "Manufacturer: FABCO — fabco.net",
      "Specialty item — sourced Off-The-Grid (non-GPO)",
    ],
    specs: {
      "Manufacturer": "FABCO",
      "Website": "fabco.net/products/neuro-patties",
      "Material": "Rayon — latex-free",
      "Sterility": "Sterile",
      "Radiopaque": "Yes — X-ray detectable",
      "Locator String": "Securely attached",
      "Card Format": "Count-verification card",
      "Standard Pack": "10 patties/card · 1 card/pouch · 20 pouches/box",
      "Box Count": "200 patties / box",
      "Sourcing": "Specialty · Off-The-Grid (non-GPO)",
      "Availability": "In Stock — pricing confirmed at RFQ acceptance",
    },
    applications: [
      "Neurosurgery — cortical protection, hemostasis, tissue retraction",
      "Spine surgery — dural protection and CSF containment",
      "ENT / ophthalmology — delicate soft-tissue absorption",
      "General & pediatric surgery — microsurgery and precision hemostasis",
    ],
    // RFQ item — no fixed price. Marketplace card renders "Request pricing" when both prices are 0.
    unit_price: 0,
    case_price: 0,
    pack: "Box of 200 (10/card · 20 pouches/box)",
    price_label: "Request pricing — specialty OTG item · In Stock",
    variants: [
      { sku: "35500010", size: "¼ × ¼ in (6 × 6 mm)",     unit_price: 0, case_price: 0 },
      { sku: "35501110", size: "½ × ½ in (13 × 13 mm)",   unit_price: 0, case_price: 0 },
      { sku: "35501310", size: "½ × 1 in (13 × 25 mm)",   unit_price: 0, case_price: 0 },
      { sku: "35501410", size: "½ × 1½ in (13 × 38 mm)",  unit_price: 0, case_price: 0 },
      { sku: "35501510", size: "½ × 2 in (13 × 51 mm)",   unit_price: 0, case_price: 0 },
      { sku: "35501610", size: "½ × 3 in (13 × 76 mm)",   unit_price: 0, case_price: 0 },
      { sku: "35502210", size: "¾ × ¾ in (19 × 19 mm)",   unit_price: 0, case_price: 0 },
      { sku: "35503310", size: "1 × 1 in (25 × 25 mm)",   unit_price: 0, case_price: 0 },
      { sku: "35503610", size: "1 × 3 in (25 × 76 mm)",   unit_price: 0, case_price: 0 },
    ],
    productUrl: "https://www.fabco.net/products/neuro-patties",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/facility-supplies/fabco-neuro-patties-spec-sheet.pdf`,
    rfq: true,
    featured: true,
    badge: "Specialty · OTG",
    faqs: [
      {
        q: "Are FABCO neuro patties latex-free?",
        a: "Yes. Every FABCO neuro patty SKU carried by Sterile Mate is 100% medical-grade rayon and latex-free. Latex is not used anywhere in the pattie, the locator string, the barium sulfate marker, or the packaging.",
      },
      {
        q: "Are FABCO neuro patties X-ray detectable?",
        a: "Yes. Every FABCO neuro patty is manufactured with an embedded barium sulfate radiopaque line that shows clearly on intraoperative and post-operative films. This meets Joint Commission NPSG.14 and AORN retained-surgical-item prevention requirements.",
      },
      {
        q: "What is the difference between FABCO neuro patties and American Surgical Ray-Cot®?",
        a: "Both are U.S.-made, sterile, latex-free, X-ray detectable rayon patties with a locator string, packaged 10 patties per card / 20 pouches per box (200 per box). Clinical performance is essentially interchangeable. American Surgical Ray-Cot® is the more common brand in U.S. hospitals and Sterile Mate is the authorized distributor. FABCO is stocked through our specialty (Off-The-Grid / non-GPO) channel for hospitals whose surgeons or contracts specifically require FABCO SKUs. See our full comparison at /resources/what-are-neuro-patty-sponges.",
      },
      {
        q: "Can I order FABCO neuro patties without a GPO contract?",
        a: "Yes. Sterile Mate is a non-GPO friendly distributor. No Vizient, Premier, HealthTrust, or Intalere agreement is required. We accept net-30 terms on credit approval, offer certified small-business supplier-diversity credit on every invoice, and have no minimum order for established accounts. Freight-inclusive pricing is available on 5+ box orders in TX, LA, AL, and MS.",
      },
      {
        q: "How is a box of FABCO neuro patties packaged?",
        a: "Industry-standard pack: 10 patties are wound on a count-verification card, sealed inside a sterile peel-open pouch, with 20 pouches per box — 200 patties per box. This is identical to American Surgical Ray-Cot® packaging, so case-cart pull sheets do not need to change when switching between brands.",
      },
      {
        q: "How long does it take to get a FABCO price quote?",
        a: "Sterile Mate returns a firm quote within one business day of receiving your RFQ. Because FABCO is sourced through our specialty channel, stock availability is confirmed at the same time as pricing. For time-critical orders we can also cross-quote the equivalent size in the Authorized American Surgical Ray-Cot® line — ask your account manager for the cross-reference sheet.",
      },
    ],
  },
  // Sterile Processing Products
  {
    id: "cleanread-atp",
    name: "CleanRead™ Handheld ATP Testing Device",
    category: "sterile-processing",
    brand: "Empire Bio Diagnostics",
    tagline: "15-Second Results. Documented Proof.",
    description: "Bioluminescence technology for real-time cleanliness verification. Know with certainty whether that scope is clean before it touches your next patient. Results in 15 seconds with cloud-synced compliance documentation.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/cleanread-handheld.png`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/cleanread-handheld.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/cleanread-atp-meter.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/cleanread-carousel1.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/cleanread-carousel2.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/cleanread-carousel3.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/cleanread-carousel4.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/cleanread-swabs.png`,
    ],
    features: [
      "Results in 15 seconds",
      "Bioluminescence detection technology",
      "RFID & barcode scanning for test-point tracking",
      "Wi-Fi sync with the CleanRead™ Web Portal (offline mode supported)",
      "AI-based daily test scheduling",
      "Handheld ergonomic design · rechargeable battery",
      "Joint Commission / IAHCSMM compliance documentation",
    ],
    specs: {
      "Detection Time": "15 seconds",
      "Sensitivity": "1 femtomole ATP",
      "Display": "Color LCD",
      "Connectivity": "Wi-Fi · USB · Bluetooth",
      "Manufacturer": "Empire Bio Diagnostics (EBD)",
    },
    applications: ["Endoscope cleaning verification", "Surface cleanliness testing", "Water system monitoring", "Sterile processing quality assurance"],
    productUrl: "https://ebdny.com/hand-held",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/downloads/brochures/ebd-cleanread-handheld-brochure.pdf`,
    featured: true,
    badge: "Best Seller"
  },
  {
    id: "cleanread-vib",
    name: "CleanRead™ ViB Visual Inspection Borescope",
    category: "sterile-processing",
    brand: "Empire Bio Diagnostics",
    tagline: "AI-Enhanced Visual Inspection",
    description: "Next-gen AI-powered visual inspection borescope designed for Sterile Processing. High-definition camera with 120° field of view, integrated adjustable LED lighting, and a 21-inch color display auto-detect abnormalities inside endoscope channels. 1.8mm minimum insertion diameter, 3.0-meter working length, and water-resistant / enzymatic-safe housing.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/vib-borescope.png`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/vib-borescope.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/ebd/vib-banner.png`,
    ],
    features: [
      "AI auto-detects abnormalities inside endoscope channels",
      "HD-ready — high-resolution images + live video feed",
      "120° field of view · adjustable LED brightness",
      "21-inch integrated color display",
      "1.8mm min. insertion diameter · 3.0m working length",
      "Onboard storage — archive & share images/videos",
      "Water-resistant · easy to clean with enzymatic detergent/disinfectant",
      "Variable diameters and lengths available on request",
    ],
    specs: {
      "Field of View": "120°",
      "Resolution": "HD",
      "Insertion Diameter (min)": "1.8 mm",
      "Working Length": "3.0 m (variable on request)",
      "Display": "21-inch integrated color display",
      "Storage": "Onboard + shareable exports",
      "Manufacturer": "Empire Bio Diagnostics (EBD)",
    },
    applications: [
      "Endoscope channel inspection (per ANSI/AAMI ST91, AORN, IAHCSMM, FDA)",
      "Lumen verification for cannulated instruments",
      "Bioburden / soil detection",
      "QA documentation & compliance archiving",
    ],
    productUrl: "https://ebdny.com/visual-inspection-borescope",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/downloads/brochures/ebd-vib-brochure.pdf`,
    featured: true,
    badge: "New"
  },
  {
    id: "atp-test-swabs",
    name: "CleanRead™ ATP Test Swabs",
    category: "consumables",
    brand: "Empire Bio Diagnostics",
    tagline: "High-Sensitivity Sampling",
    description: "Pre-moistened ATP test swabs designed for healthcare environments. Compatible with CleanRead Handheld device.",
    image: "https://sterilemate.com/wp-content/uploads/2025/11/atp-device.webp",
    features: [
      "Pre-moistened for consistency",
      "Individual sealed packaging",
      "Long shelf life",
      "Compatible with CleanRead devices"
    ],
    specs: {
      "Pack Size": "100 swabs",
      "Shelf Life": "18 months",
      "Storage": "Room temperature"
    },
    applications: ["Surface testing", "Water sampling", "Equipment verification"],
    productUrl: "https://sterilemate.com/atp-test-swabs/",
    badge: "Consumable"
  },
  // Water Purification Products
  {
    id: "water-softener",
    name: "Commercial Water Softener Systems — Install · Replace · Repair",
    category: "water-purification",
    brand: "Sterile Mate · Licensed TX Water Treatment",
    tagline: "No job too big or too small · Certified & Licensed by the State of Texas",
    description:
      "Full-service commercial and industrial water softener program for hospitals, ASCs, dialysis centers, dental, food service, boilers, and RO pretreatment. Our certified technicians are licensed by the State of Texas to install, replace, and repair softener vessels, control valves, brine systems, and manifolds — from single-tank Simplex builds to Duplex and Triplex alternating configurations. Vessels ship in either lightweight, corrosion-resistant FRP pressure vessels (P-Series style) or epoxy-lined carbon-steel tanks (MHC-style) with sodium-form cation exchange resin. Turn-key installations include hydro-test, salt-tank fill, controller programming, pressure/flow validation, and a signed compliance handoff.",
    image: "/images/water-softener-vessels.jpg",
    gallery: ["/images/water-softener-vessels.jpg"],
    features: [
      "State-of-Texas licensed water-treatment technicians",
      "Install · Replace · Repair — service program covers OEM warranty-out equipment",
      "Simplex, Duplex, and Triplex alternating tank configurations",
      "FRP composite or NSF 61-rated epoxy-lined carbon-steel pressure vessels",
      "Metered (demand-initiated) or timed regeneration control valves",
      "Sodium-form cation exchange resin (softening) or specialty resins on request",
      "Hydro-tested to 100 PSIG · 30–100 PSIG operating pressure · 35–110 °F",
      "Complete brine-tank + safety float + salt-grid assembly",
      "NEMA-4X electrical enclosures with digital controllers",
      "Compatible with hospital 120 VAC · 1-PH · 60 Hz service",
      "Same-day emergency service across Houston · TMC · DFW · San Antonio · Austin",
    ],
    specs: {
      "Service Scope": "Install · Replace · Repair · Preventive Maintenance",
      "Licensing": "State of Texas Water Treatment Technicians",
      "Configurations": "Simplex · Duplex · Triplex alternating",
      "Vessel Materials": "FRP composite (P-Series) or NSF-61 epoxy-lined carbon steel (MHC-style)",
      "Capacity Range": "30,000 – 500,000+ grains per tank",
      "Regeneration": "Metered / Demand or Time-Clock",
      "Operating Pressure": "30 – 100 PSIG (hydro-tested to 100 PSIG)",
      "Operating Temperature": "35 – 110 °F",
      "Electrical": "120 VAC · 1-PH · 60 Hz",
      "Controllers": "Marlo MX-III · Fleck · Autotrol · Clack",
      "Enclosures": "NEMA-4X",
      "Coverage Area": "TX · LA · AL · MS (Gulf region)",
    },
    applications: [
      "Hospital pre-treatment (RO / DI feed conditioning)",
      "Sterile Processing Department (SPD) water",
      "Dialysis clinic feed water",
      "Boiler feed & steam-plant makeup",
      "Laboratory feed water",
      "Ambulatory Surgery Centers",
      "Dental & veterinary facilities",
      "Commercial kitchen / food service",
      "Equipment protection & scale prevention",
    ],
    productUrl: "https://sterilemate.com/water-softeners/",
    featured: true,
    badge: "TX Licensed",
  },
  {
    id: "megatron-mr",
    name: "Advantage Controls MegaTron MR — Reverse Osmosis Controller",
    category: "water-purification",
    brand: "Advantage Controls",
    tagline: "Industry-leading RO controller · WebAdvantage® web monitoring · BACnet & Modbus",
    description:
      "The MegaTron MR commercial and industrial RO controller is the industry-leading purpose-built unit combining the widest range of inputs and outputs with a customizable touch screen and web-based, BACnet, and Modbus monitoring and reporting. Advantage Controls can package a MegaTron MR with motor controls, variable-speed drives, and much more to meet large or small applications in their UL-508 panel shop. The MegaTron MR can be configured to monitor and control every aspect and reading of an RO system, while dealers use WebAdvantage® for remote access, alarming, and reporting.",
    image: "https://www.advantagecontrols.com/wp-content/uploads/2024/11/megatron_mr.png",
    gallery: [
      "https://www.advantagecontrols.com/wp-content/uploads/2024/11/megatron_mr.png",
      "https://www.advantagecontrols.com/wp-content/uploads/2024/10/webadv_ro_devices.png",
      "https://www.advantagecontrols.com/wp-content/uploads/2024/10/webadv_ro_graph.png",
      "https://www.advantagecontrols.com/wp-content/uploads/2024/10/webadv_RO_graphical_loop.jpg",
    ],
    features: [
      "WebAdvantage® web monitoring and reporting built-in",
      "Up to 12 customizable digital inputs",
      "Up to 32 customizable analog inputs",
      "Up to 9 totalizing flow inputs",
      "Customizable graphics and calculations",
      "Multiple flush activation logic",
      "Multiple RO activation logic",
      "Total run-time alarming",
      "Notepad manually-entered history",
      "BACnet and Modbus options",
      "UL-508 panel shop customizations",
      "Compatible with motor controls, variable-speed drives, and full RO skid integrations",
    ],
    specs: {
      "Manufacturer": "Advantage Controls · Muskogee, OK",
      "Controller Type": "Commercial / Industrial RO Controller",
      "Digital Inputs": "Up to 12 (customizable)",
      "Analog Inputs": "Up to 32 (customizable)",
      "Flow Inputs": "Up to 9 totalizing",
      "Interface": "Customizable touch screen",
      "Remote Access": "WebAdvantage® web-based monitoring",
      "Building Automation": "BACnet & Modbus options",
      "Panel Certification": "UL-508 panel shop customizations",
      "Data Sheet": "https://www.advantagecontrols.com/wp-content/uploads/datasheets/D-MegaTronMR.pdf",
      "Manual": "https://www.advantagecontrols.com/wp-content/uploads/manuals/M-MegaTronMR.pdf",
    },
    applications: [
      "Hospital reverse-osmosis skids",
      "Dialysis clinic RO monitoring",
      "Sterile Processing water systems",
      "Commercial / industrial RO controllers",
      "Building-automation integration (BACnet / Modbus)",
      "OEM RO skid packagers",
    ],
    productUrl: "https://www.advantagecontrols.com/controllers/megatron-mr/",
    brochureUrl: "https://www.advantagecontrols.com/wp-content/uploads/datasheets/D-MegaTronMR.pdf",
    badge: "WebAdvantage®",
  },
  // ─────────────────────────────────────────────────────────────────────
  // Hydronix TRITTON IXC™ — ROS-IXC Series Commercial RO Systems
  // 5 model variants, each sold as a separate SKU. Shared image, gallery,
  // features, and control-box content; per-model specs (dimensions, ports,
  // GPD, membranes, pump capacity) are set in the .map() below.
  // Source: hydronixwater.com/commercial-reverse-osmosis-water-purification-systems
  // ─────────────────────────────────────────────────────────────────────
  ...([
    {
      sku: "ROS-IXC-1SS-T",
      slug: "ros-ixc-1ss-t",
      membranes: 1,
      productionGpd: "2,100 – 2,400",
      productGpm: "1.45 – 1.6",
      feedGpm: "5",
      pumpGpm: "15",
      inletPort: '3/4"',
      productOutlet: '1/2"',
      wasteOutlet: '1/2"',
      dimensions: '20" × 28" × 60"',
    },
    {
      sku: "ROS-IXC-2SS-T",
      slug: "ros-ixc-2ss-t",
      membranes: 2,
      productionGpd: "4,200 – 4,800",
      productGpm: "2.9 – 3.2",
      feedGpm: "8",
      pumpGpm: "15",
      inletPort: '3/4"',
      productOutlet: '1/2"',
      wasteOutlet: '1/2"',
      dimensions: '20" × 28" × 60"',
    },
    {
      sku: "ROS-IXC-3SS-T",
      slug: "ros-ixc-3ss-t",
      membranes: 3,
      productionGpd: "6,300 – 7,200",
      productGpm: "4.3 – 4.8",
      feedGpm: "10",
      pumpGpm: "15",
      inletPort: '1"',
      productOutlet: '3/4"',
      wasteOutlet: '3/4"',
      dimensions: '25" × 28" × 60"',
    },
    {
      sku: "ROS-IXC-4SS-T",
      slug: "ros-ixc-4ss-t",
      membranes: 4,
      productionGpd: "8,400 – 9,600",
      productGpm: "5.8 – 6.4",
      feedGpm: "20",
      pumpGpm: "25",
      inletPort: '1"',
      productOutlet: '3/4"',
      wasteOutlet: '3/4"',
      dimensions: '25" × 28" × 60"',
    },
    {
      sku: "ROS-IXC-6SS-T",
      slug: "ros-ixc-6ss-t",
      membranes: 6,
      productionGpd: "14,400 – 15,600",
      productGpm: "10 – 10.8",
      feedGpm: "25",
      pumpGpm: "25",
      inletPort: '1"',
      productOutlet: '1"',
      wasteOutlet: '1"',
      dimensions: '25" × 50" × 47"',
    },
  ].map((m) => ({
    id: m.slug,
    name: `Hydronix ROS-IXC ${m.membranes}SS-T — ${m.productionGpd.split(" – ")[1]} GPD Commercial Reverse Osmosis System`,
    category: "water-purification",
    brand: "Hydronix · TRITTON IXC™",
    tagline: `${m.productionGpd} GPD · ${m.membranes} membrane element${m.membranes > 1 ? "s" : ""} · 50–60% Recovery`,
    description:
      `The Hydronix ${m.sku} is a TRITTON IXC™ commercial reverse osmosis water purification system engineered for drinking water, food service, hotels, restaurants, healthcare facilities, and manufacturing applications. Simple operation and ease of maintenance are built into the design of each system while considering the importance of not occupying a large footprint. This ${m.membranes}-membrane configuration delivers ${m.productionGpd} GPD (${m.productGpm} GPM product flow) at 50–60% recovery, driven by a Multistage Goulds pump on a stainless-steel 304 housing. Ships fully assembled — requires zero programming: connect feed, waste, and product lines, power up, and the digital control panel displays real-time TDS, pump status, low-pressure alerts, flush activation, and full-tank state.`,
    image: "https://hydronixwater.com/wp-content/uploads/2019/10/ROA-IXC-Series-Tritton.jpg",
    gallery: [
      "https://hydronixwater.com/wp-content/uploads/2019/10/ROA-IXC-Series-Tritton.jpg",
      "https://hydronixwater.com/wp-content/uploads/2018/08/ROA-IXC-Series-Tritton-control-box-500x494.jpg",
      "https://hydronixwater.com/wp-content/uploads/2018/11/ROS-IXC-thumb.jpg",
    ],
    features: [
      `${m.membranes}× Hydron high-rejection BW-4040 RO membrane${m.membranes > 1 ? "s" : ""} (4" × 40")`,
      "Multistage Goulds pump with stainless-steel 304 membrane housing (FRP optional)",
      "Digital TDS display monitors product water quality in real time (PPM)",
      "Control box with time delay, level switch, off/auto selector, low-pressure switch, and 24 A thermal relay",
      "Panel-mount flow meters + 0–100 PSI and 0–300 PSI glycerine-filled pressure gauges",
      "Stainless-steel 316 needle valve (NV-12SS316) for waste & recirculation",
      "High-pressure quick connectors and NC 120 V brass solenoid valve",
      "20\" 5-micron polyspun sediment pre-filter housing included",
      "Powder-coated steel frame · bent-panel format · fully assembled — zero programming required",
      "Optional fast flush mode extends membrane life",
    ],
    specs: {
      "Part Number": m.sku,
      "Production (GPD)": `${m.productionGpd} GPD*`,
      "Product Flow (GPM)": `${m.productGpm} GPM`,
      "Feed Flow (GPM)": `${m.feedGpm} GPM`,
      "Membranes": `4" × 40" (${m.membranes})`,
      "Membrane Type": "Hydron BW-4040 High-Rejection",
      "Recovery Range": "50 – 60 %",
      "Max High-Pressure Pump Capacity": `${m.pumpGpm} GPM`,
      "Inlet Port": m.inletPort,
      "Product Outlet Port": m.productOutlet,
      "Waste Outlet Port": m.wasteOutlet,
      "System Dimensions": m.dimensions,
      "Electrical Power Supply": "220 V · 1-PH",
      "Membrane Housing": "Stainless Steel 304 (FRP optional)",
      "Pump": "Multistage Goulds",
      "Frame": "Powder-coated steel · bent-panel",
      "Data Sheet": "https://hydronixwater.com/dl/data_sheets/ROS-IXC_DS.pdf",
    },
    applications: [
      "Healthcare facility drinking water",
      "Sterile Processing feed water pre-treatment",
      "Food service & commercial kitchens",
      "Hotels & hospitality",
      "Restaurants",
      "Light manufacturing",
      "Laboratory feed water",
    ],
    productUrl: "https://hydronixwater.com/commercial-reverse-osmosis-water-purification-systems/",
    brochureUrl: "https://hydronixwater.com/dl/data_sheets/ROS-IXC_DS.pdf",
    badge: `${m.membranes} Membrane${m.membranes > 1 ? "s" : ""}`,
    featured: m.membranes === 3,
    note: "* Production rate assumes feed water < 1,000 PPM, 77 °F feed temperature, and SDI < 5. Suitable pretreatment is required to remove membrane-damaging substances.",
  }))),
  // ─────────────────────────────────────────────────────────────────────
  // Hydronix Water Technology — Full Product Family lineup (8 families)
  // Scraped from hydronixwater.com. Each family = a single marketplace SKU
  // with a Nano-Banana-generated hero photo (Hydronix's own images are
  // hotlink-protected, so we host our own). PDF catalogs live at
  // productUrl. Sold as request-quote items (case pricing set via inquiry).
  // ─────────────────────────────────────────────────────────────────────
  ...([
    {
      slug: "hydron-ro-membranes",
      name: "Hydron Membrane Technology™ — Reverse Osmosis Membranes",
      tagline: "Residential · Commercial · Industrial · Treated / Brackish / Sea Water · LP & ULP models",
      image: "/images/hydronix/hydron-ro-membranes.jpg",
      description:
        "Hydron Membrane Technology™ delivers a full lineup of reverse osmosis membranes for residential, commercial, and industrial applications. Includes treated-water, brackish-water, sea-water, low-pressure (LP) and ultra-low-pressure (ULP) models — engineered for high rejection, long service life, and consistent flow across the full pressure envelope.",
      features: [
        "Full residential / commercial / industrial spiral-wound RO membrane portfolio",
        "Treated water, brackish water, and sea water models",
        "Low-pressure (LP) and ultra-low-pressure (ULP) energy-saving elements",
        "High-rejection Hydron BW-4040 flagship element (used in Tritton IXC systems)",
        "NSF/ANSI certification on core SKUs",
        "OEM & distributor pricing available",
      ],
      specs: {
        "Category": "Reverse Osmosis Membranes",
        "Sizes": "2.5\" × 21\" · 2.5\" × 40\" · 4\" × 40\" · 4\" × 80\" · 8\" × 40\"",
        "Model Types": "Standard · LP · ULP · Brackish · Sea Water",
        "Certification": "NSF/ANSI (core SKUs)",
        "Catalog": "https://hydronixwater.com/wp-content/uploads/2025/04/Hydron_Catalog_2025.pdf",
      },
      productUrl: "https://hydronixwater.com/wp-content/uploads/2025/04/Hydron_Catalog_2025.pdf",
      badge: "Hydron",
    },
    {
      slug: "hdx-filtration-components",
      name: "Hydronix Water Technology™ (HDX) — Filters, Housings & Vessels",
      tagline: "Sediment · pleated · string-wound · carbon block · housings · pressure vessels",
      image: "/images/hydronix/hdx-filters.jpg",
      description:
        "The core Hydronix HDX product family covers everything needed to build and service a water filtration system: sediment filters, pleated filters, string-wound filters, granular and extruded carbon block filters, filter housings, pressure vessels, and a broad range of filtration components. From individual parts to complete pre-built systems.",
      features: [
        "5- and 20-micron polyspun sediment filters",
        "Pleated cellulose and polyester filters",
        "String-wound depth filters",
        "GAC (granular activated carbon) and carbon block filters",
        "Standard and Big-Blue housings (10\", 20\", high-flow)",
        "FRP and stainless-steel pressure vessels",
        "Full replacement-parts catalog for service & repair",
      ],
      specs: {
        "Category": "Filter Cartridges · Housings · Vessels",
        "Cartridge Sizes": "9-7/8\", 10\", 20\", high-flow",
        "Micron Ratings": "1, 5, 10, 20, 50 µm (sediment) · sub-micron carbon options",
        "Housings": "Standard and Big-Blue",
        "Catalog": "https://hydronixwater.com/wp-content/uploads/2025/04/HDX_Catalog_2025.pdf",
      },
      productUrl: "https://hydronixwater.com/wp-content/uploads/2025/04/HDX_Catalog_2025.pdf",
      badge: "HDX",
    },
    {
      slug: "aquatrol-valves",
      name: "Aquatrol™ — Control Valves, Tanks, Distributors & Chemical Feed",
      tagline: "Softener / filter control valves · pressure tanks · distributors · resin · carbon · chemical feed pumps",
      image: "/images/hydronix/aquatrol-control-valve.jpg",
      description:
        "Aquatrol™ delivers control valves, pressure tanks, distributors, small business-and-lateral kits, softener resin, filtration media (carbon, gravel), chemical feed pumps, and every accessory needed to assemble residential, commercial, and industrial water treatment systems.",
      features: [
        "Metered demand-initiated and time-clock control valves",
        "FRP pressure tanks (residential to industrial sizes)",
        "Sodium-form cation exchange resin (softening) and specialty resins",
        "Activated carbon, garnet, and gravel filtration media",
        "Distributor tubes, small business-and-lateral kits, riser assemblies",
        "LMI-compatible chemical feed metering pumps",
        "Full accessory catalog for OEM system builds",
      ],
      specs: {
        "Category": "Softener/Filter Valves · Tanks · Media · Chemical Feed",
        "Valve Types": "Metered · Time-Clock · Fleck/Autotrol/Clack-compatible",
        "Tank Sizes": "8\" × 44\" up to 24\" × 72\"+",
        "Feed Pumps": "LMI-compatible chemical dosing pumps",
        "Catalog": "https://hydronixwater.com/wp-content/uploads/2025/04/AQT_Valves_Systems_Catalog_2025-1.pdf",
      },
      productUrl: "https://hydronixwater.com/wp-content/uploads/2025/04/AQT_Valves_Systems_Catalog_2025-1.pdf",
      badge: "Aquatrol™",
    },
    {
      slug: "hydrofit-quick-connect-fittings",
      name: "Hydrofit™ — Quick Connect Fittings & Tubing",
      tagline: "Push-fit unions · elbows · tees · shut-off valves · food-grade tubing",
      image: "/images/hydronix/hydrofit-fittings.jpg",
      description:
        "Hydrofit™ Quick Connect Fittings and tubing are high-quality engineered products designed to give a reliable connector for a reliable connection every time. Hydrofit fittings provide all the typical benefits and ease of use of a quick-connect fitting while providing exceptional value.",
      features: [
        "Push-fit quick-connect design (no tools required)",
        "Straight unions, elbows, tees, Y-splitters, and shut-off valves",
        "1/4\", 3/8\", 1/2\" tube sizes",
        "Food-grade polyethylene tubing in coils and cut lengths",
        "NSF/ANSI 61 compliant materials",
        "Compatible with all standard RO and drinking-water systems",
        "Retrofit-friendly — replace any push-fit brand with Hydrofit",
      ],
      specs: {
        "Category": "Push-Fit Water Fittings & Tubing",
        "Tube OD Sizes": "1/4\", 3/8\", 1/2\"",
        "Materials": "NSF/ANSI 61 polymer body · food-grade PE tubing",
        "Pressure Rating": "Up to 150 PSI",
        "Catalog": "https://hydronixwater.com/dl/catalogs/Hydrofit_catalog.pdf",
      },
      productUrl: "https://hydronixwater.com/dl/catalogs/Hydrofit_catalog.pdf",
      badge: "Hydrofit™",
    },
    {
      slug: "polaris-uv-sterilizers",
      name: "Polaris Scientific Ultraviolet™ — UV Sterilization Systems (see 14-model catalog below)",
      tagline: "UVA Series · 1 – 200 GPM · residential · light-commercial · lab · food · rinse-loop applications",
      image: "/images/hydronix/polaris-uv-system.jpg",
      description:
        "Polaris Scientific Ultraviolet™ Systems have a sleek, compact design and provide cost-effective ultraviolet water treatment for low- to medium-flow applications. Ideal for residential and light-commercial RO systems, water vending machines, food industry, laboratory, final electronic-component rinsing, and recirculation loops. Little change in water temperature even after prolonged no-flow periods.",
      features: [
        "UVA Series (residential UVA-1C → UVA-12C · commercial UVA-24B → UVA-200B)",
        "Validated UV lamps with 9,000-hour rated life",
        "Compact units require minimum space",
        "304 or 316 stainless-steel reactor chamber, polished for lab / medical use",
        "Easy servicing — no need to turn off water flow to change lamp",
        "Audible ballast with LED lamp-out indicator",
        "254 nm wavelength · 30 mJ/cm² UV dose",
        "Max pressure 125 PSI · operating temperature 35 – 104 °F",
        "Rated life: 9,000 hours",
        "5-year UV chamber warranty · 1-year electrical & lamp warranty",
      ],
      specs: {
        "Category": "UV Water Sterilization · UVA Series",
        "Flow Range": "1 – 200 GPM (14 models)",
        "Chamber Material": "304 or 316 Stainless Steel",
        "Wavelength": "254 nm",
        "UV Dose": "30 mJ/cm²",
        "Max Pressure": "125 PSI",
        "Operating Temp": "35 – 104 °F (1.67 – 40 °C)",
        "Lamp Life": "9,000 hours rated",
        "Residential Data Sheet": "https://sterilemate.com/wp-content/uploads/2025/10/UVA-1C-UVA-12C_DS.pdf",
        "Commercial Data Sheet": "https://sterilemate.com/wp-content/uploads/2025/10/UVA-24B-UVA-200B_DS.pdf",
      },
      productUrl: "https://sterilemate.com/standard-uv-systems/",
      badge: "Polaris UV Series",
    },
    {
      slug: "shok-blok-protection-valve",
      name: "Shok Blok™ — Filter Protection Valve",
      tagline: "Water hammer protection · pressure regulation for filters, ice makers, coffee, RO",
      image: "/images/hydronix/shok-blok-valve.jpg",
      description:
        "Shok Blok™ Filter Protection Valves are designed to limit water pressure entering a water filter, water cooler, drink dispenser, ice maker, coffee machine, or reverse-osmosis system. Shok Blok helps protect water-filtration devices from water hammer and preserves the structural integrity of any drinking-water system.",
      features: [
        "Limits incoming water pressure to safe filter/RO levels",
        "Absorbs water hammer shock spikes",
        "Compatible with water filters, coolers, dispensers, ice makers, coffee machines, RO systems",
        "Compact brass body — installs in-line with 3/8\" or 1/2\" tubing",
        "No electricity required — mechanical device",
        "Extends filter and appliance service life",
        "Simple field installation — protects OEM warranties",
      ],
      specs: {
        "Category": "Inline Pressure Regulator / Water Hammer Protection",
        "Body Material": "Brass",
        "Fitting Sizes": "3/8\" and 1/2\" tube-compatible",
        "Application": "Filters · coolers · ice makers · coffee · RO",
        "Data Sheet": "https://hydronixwater.com/dl/data_sheets/SB_DS.pdf",
      },
      productUrl: "https://hydronixwater.com/dl/data_sheets/SB_DS.pdf",
      badge: "Shok Blok™",
    },
    {
      slug: "hydro-guard-drinking-water",
      name: "Hydro Guard™ — Total Care Drinking Water Systems",
      tagline: "Point-of-use residential + light-commercial drinking water · better than bottled",
      image: "/images/hydronix/hydro-guard-drinking.jpg",
      description:
        "The Hydro Guard™ Total Care line of drinking-water systems offers a high-quality, easy-to-use, cost-effective permanent solution to any family's or facility's drinking-water needs. They provide state-of-the-art water filtration technology and better-than-bottled water quality by delivering safe, delicious, unlimited water at your fingertips — filtered at the point of use, not days, weeks, or months before.",
      features: [
        "Point-of-use filtration (POU) — filters at the tap, not upstream",
        "3-, 4-, and 5-stage system configurations",
        "Sediment · GAC · carbon block · optional RO / UV polishing",
        "NSF/ANSI-listed cartridges on core builds",
        "Dedicated drinking-water faucet included",
        "Long cartridge service life — annual maintenance",
        "Perfect fit for break rooms, waiting rooms, family homes",
      ],
      specs: {
        "Category": "Drinking Water Systems (POU)",
        "Stages": "3 / 4 / 5-stage configurations",
        "Included Faucet": "Yes (dedicated dispenser)",
        "Cartridge Compatibility": "Standard 10\" cartridges",
        "Catalog": "https://hydronixwater.com/wp-content/uploads/2025/09/HDGP_Catalog_2025_web.pdf",
      },
      productUrl: "https://hydronixwater.com/wp-content/uploads/2025/09/HDGP_Catalog_2025_web.pdf",
      badge: "Hydro Guard™",
    },
    {
      slug: "flowlok-leak-protection",
      name: "Flowlok™ — Leak Protection Systems",
      tagline: "Automatic water shutoff on leak detection · no electricity required · 15-min install",
      image: "/images/hydronix/flowlok-leak-protection.jpg",
      description:
        "Flowlok™ Leak Protection Systems are designed to help customers safeguard against possible leaks from their water filtration system. Flowlok™ leak protection products help consumers by shutting off the water supply to their filtration system if water from a leak is detected. The Flowlok™ system requires no electricity, is an economical device that every water filtration owner should have to help minimize water damage, and installs (or retrofits) in no more than 15 minutes.",
      features: [
        "Automatic shut-off when a leak is detected",
        "No electricity required — mechanical trigger",
        "15-minute installation or retrofit",
        "Economical insurance against costly water damage",
        "Compatible with RO systems, water filters, coffee machines, ice makers",
        "Flat leak sensor pad + inline shut-off valve",
        "Reset-and-reuse after the leak is repaired",
      ],
      specs: {
        "Category": "Leak Detection & Automatic Shut-Off",
        "Power": "None required (mechanical)",
        "Install Time": "≤ 15 minutes",
        "Reset": "Field-resettable after leak repair",
        "Data Sheet": "https://hydronixwater.com/dl/data_sheets/FLK_DS.pdf",
      },
      productUrl: "https://hydronixwater.com/dl/data_sheets/FLK_DS.pdf",
      badge: "Flowlok™",
    },
  ].map((p) => ({
    id: p.slug,
    name: p.name,
    category: "water-purification",
    brand: "Hydronix Water Technology",
    tagline: p.tagline,
    description: p.description,
    image: p.image,
    gallery: [p.image],
    features: p.features,
    specs: p.specs,
    applications: [
      "Hospital water treatment programs",
      "Sterile Processing feed water",
      "Dialysis clinic pretreatment",
      "Laboratory feed water",
      "Commercial kitchens & food service",
      "OEM system integrators",
      "Building service & maintenance contractors",
    ],
    productUrl: p.productUrl,
    brochureUrl: p.productUrl,
    badge: p.badge,
  }))),
  // ─────────────────────────────────────────────────────────────────────
  // Polaris Scientific Ultraviolet™ — UVA Series individual SKUs (14 models)
  // Scraped from sterilemate.com/standard-uv-systems/ — residential + commercial.
  // Each row of the spec table becomes its own marketplace SKU so buyers can
  // add the exact model they need to the quote cart.
  // ─────────────────────────────────────────────────────────────────────
  ...([
    { sku: "UVA-1C",   flow: "1",   lpm: "3.8",    watts: "10W", lamps: 1, port: '1/4" MNPT',  length: '10.47"', width: '2"',    tier: "Residential", audible: "Buzzer" },
    { sku: "UVA-2C",   flow: "2",   lpm: "7.57",   watts: "14W", lamps: 1, port: '1/4" MNPT',  length: '13.74"', width: '2.5"',  tier: "Residential", audible: "Buzzer" },
    { sku: "UVA-4C",   flow: "4",   lpm: "15.14",  watts: "17W", lamps: 1, port: '1/2" MNPT',  length: '15.50"', width: '2.5"',  tier: "Residential", audible: "Buzzer" },
    { sku: "UVA-6C",   flow: "6",   lpm: "22.71",  watts: "24W", lamps: 1, port: '1/2" MNPT',  length: '23.23"', width: '2.5"',  tier: "Residential", audible: "Indicator w/ alarm" },
    { sku: "UVA-8C",   flow: "8",   lpm: "30.28",  watts: "32W", lamps: 1, port: '3/4" MNPT',  length: '27.87"', width: '2.5"',  tier: "Residential", audible: "Indicator w/ alarm" },
    { sku: "UVA-12C",  flow: "12",  lpm: "45.52",  watts: "39W", lamps: 1, port: '3/4" MNPT',  length: '36.81"', width: '2.5"',  tier: "Residential", audible: "Indicator w/ alarm" },
    { sku: "UVA-24B",  flow: "24",  lpm: "90.85",  watts: "39W", lamps: 2, port: '1" MNPT',    length: '36.93"', width: '3.5"',  tier: "Commercial",  audible: "Indicator w/ alarm" },
    { sku: "UVA-36B",  flow: "36",  lpm: "136.27", watts: "39W", lamps: 3, port: '1.5" MNPT',  length: '36.93"', width: '5.5"',  tier: "Commercial",  audible: "Indicator w/ alarm" },
    { sku: "UVA-50B",  flow: "50",  lpm: "189.27", watts: "39W", lamps: 4, port: '1.5" MNPT',  length: '36.93"', width: '5.5"',  tier: "Commercial",  audible: "Indicator w/ alarm" },
    { sku: "UVA-60B",  flow: "60",  lpm: "227.12", watts: "39W", lamps: 5, port: '2" or 2.5" Flange', length: '36.93"', width: '5.5"', tier: "Commercial", audible: "Indicator w/ alarm" },
    { sku: "UVA-80B",  flow: "80",  lpm: "302.83", watts: "39W", lamps: 6, port: '2" or 2.5" Flange', length: '36.93"', width: '6.26"', tier: "Commercial", audible: "Indicator w/ alarm" },
    { sku: "UVA-100B", flow: "100", lpm: "378.54", watts: "39W", lamps: 8, port: '2" or 2.5" Flange', length: '36.93"', width: '8.62"', tier: "Commercial", audible: "Indicator w/ alarm" },
    { sku: "UVA-150B", flow: "150", lpm: "567.81", watts: "87W", lamps: 6, port: '3" Flange',        length: '36.93"', width: '8.62"', tier: "Commercial", audible: "Indicator w/ alarm" },
    { sku: "UVA-200B", flow: "200", lpm: "757.08", watts: "87W", lamps: 8, port: '4" Flange',        length: '36.93"', width: '8.62"', tier: "Commercial", audible: "Indicator w/ alarm" },
  ].map((m, i) => {
    // Local Polaris UV product image (generated) — all 14 models share the
    // same family photo since sterilemate.com hotlink protection blocks
    // remote loads.
    const heroImg = "/images/hydronix/polaris-uv-system.jpg";
    return {
      id: m.sku.toLowerCase(),
      name: `Polaris Scientific ${m.sku} — ${m.flow} GPM UV Sterilization System (${m.tier})`,
      category: "water-purification",
      brand: "Polaris Scientific Ultraviolet™ · UVA Series",
      tagline: `${m.flow} GPM (${m.lpm} L/min) · ${m.lamps} × ${m.watts} lamp${m.lamps > 1 ? "s" : ""} · Stainless 304 chamber · ${m.port}`,
      description:
        `The Polaris Scientific ${m.sku} is a ${m.tier.toLowerCase()} UV sterilization system rated for ${m.flow} GPM (${m.lpm} L/min) with ${m.lamps} × ${m.watts} high-output low-pressure mercury vapor lamp${m.lamps > 1 ? "s" : ""} and a polished stainless steel 304 reactor chamber. Ideal for RO polishing, water vending, food service, laboratory, final electronic-component rinsing, and recirculation loops. Ships with LED lamp-out indicator and ${m.audible.toLowerCase()} alerting. Little change in water temperature even after prolonged no-flow periods.`,
      image: heroImg,
      gallery: [heroImg],
      features: [
        `${m.flow} GPM rated flow (${m.lpm} L/min)`,
        `${m.lamps} × ${m.watts} validated UV lamp${m.lamps > 1 ? "s" : ""} (9,000-hour rated life)`,
        `Port size: ${m.port}`,
        "Polished stainless-steel 304 reactor chamber (medical / laboratory grade)",
        "LED lamp-out indicator with " + m.audible.toLowerCase() + " alerting",
        "254 nm wavelength · 30 mJ/cm² UV dose",
        "Max pressure 125 PSI · operating 35 – 104 °F",
        "Open-end quartz sleeve with stainless-steel gland nuts",
        "Easy servicing — no need to shut off water flow to change lamp",
        "Compact design · minimum footprint",
        "5-year UV chamber warranty · 1-year electrical & lamp warranty",
      ],
      specs: {
        "Part Number": m.sku,
        "Tier": m.tier,
        "Flow Rate": `${m.flow} GPM (${m.lpm} L/min)`,
        "Lamp Configuration": `${m.lamps} × ${m.watts}`,
        "# of Lamps": String(m.lamps),
        "Port Size (I/O)": m.port,
        "Water Chamber": "Stainless 304",
        "Length": m.length,
        "Width": m.width,
        "Visual Warning": "LED",
        "Audible Warning": m.audible,
        "Wavelength": "254 nm",
        "UV Dose": "30 mJ/cm²",
        "Max Pressure": "125 PSI",
        "Operating Temp": "35 – 104 °F (1.67 – 40 °C)",
        "Lamp Rated Life": "9,000 hours",
        "Data Sheet": m.tier === "Residential"
          ? "https://sterilemate.com/wp-content/uploads/2025/10/UVA-1C-UVA-12C_DS.pdf"
          : "https://sterilemate.com/wp-content/uploads/2025/10/UVA-24B-UVA-200B_DS.pdf",
      },
      applications: [
        "Post-RO polishing (hospital, dialysis, SPD)",
        "Water vending & bottling",
        "Food & beverage lines",
        "Laboratory feed water",
        "Final electronic-component rinsing",
        "Recirculation loops",
        m.tier === "Residential" ? "Residential drinking water" : "Commercial building water treatment",
      ],
      productUrl: "https://sterilemate.com/standard-uv-systems/",
      brochureUrl: m.tier === "Residential"
        ? "https://sterilemate.com/wp-content/uploads/2025/10/UVA-1C-UVA-12C_DS.pdf"
        : "https://sterilemate.com/wp-content/uploads/2025/10/UVA-24B-UVA-200B_DS.pdf",
      badge: `${m.flow} GPM`,
      featured: m.sku === "UVA-24B",
    };
  })),
  // EVS & Janitorial Products
  {
    id: "cordova-4522-green-nitrile",
    name: "Cordova 4522 \u2014 Green Nitrile, Unlined, 22-mil, Pebble Grip, 18-Inch Glove",
    category: "evs-janitorial",
    subcategory: "Chemical Resistant Gloves",
    brand: "Cordova Safety",
    tagline: "22-mil Premium Unsupported Nitrile \u00b7 18\u2033 Gauntlet",
    description: "Premium unsupported nitrile glove for heavy chemical handling, HazMat cleanup, food processing, and petrochemical work. Made with FDA-accepted materials (21 CFR 170\u2013199) for direct food contact. Pebble grip retains bite when wet with oils or hydrocarbons. Full 18-inch gauntlet protects the forearm against splashes.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/gloves/cordova-4522-green-nitrile.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/gloves/cordova-4522-green-nitrile.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/gloves/cordova-4522-green-nitrile-alt.jpg`,
    ],
    features: [
      "Premium unsupported nitrile \u2014 heavy 22-mil (0.56 mm) palm thickness",
      "18-inch full-forearm gauntlet length",
      "Pebble grip retains traction in wet / oily conditions",
      "Unlined for maximum dexterity and easy donning / doffing",
      "Made with FDA-accepted materials for direct food-contact use",
      "CE EN388 tested \u2014 Abrasion 4 / Cut 1 / Tear 0 / Puncture 1",
      "Sold by the pair \u00b7 12 pairs per dozen \u00b7 6 dozen per case",
    ],
    specs: {
      "Model": "Cordova 4522",
      "Material": "Unsupported nitrile (synthetic rubber)",
      "Thickness": "22 mil (0.56 mm)",
      "Length": "18 inches (46 cm)",
      "Grip": "Pebble embossed \u2014 palm & fingers",
      "Lining": "Unlined",
      "Color": "Green",
      "Sizes": "S / M / L / XL / 2XL",
      "Packaging": "12 pairs per dozen \u00b7 6 dozen per case (72 pairs / case)",
      "Standards": "CE EN388, FDA 21 CFR 170\u2013199, CE Directive 89/109/EEC",
      "Prop 65": "No warning required",
    },
    applications: [
      "Food processing (red meat, poultry, dairy)",
      "Petrochemical \u2014 handling hydrocarbons, oil, grease, gasoline",
      "Janitorial / EVS chemical restocking",
      "HazMat cleanup and decontamination",
      "Printing, painting, and solvent work",
      "Agricultural chemical handling",
    ],
    unit_price: 189.00,
    case_price: 1134.00,
    pack: "12 pairs / dozen",
    price_label: "$189.00 / dozen \u00b7 $1,134.00 / case (6 dozen) \u00b7 freight not included",
    variants: [
      { sku: "4522S",   size: "Small",    unit_price: 189.00, case_price: 1134.00 },
      { sku: "4522M",   size: "Medium",   unit_price: 189.00, case_price: 1134.00 },
      { sku: "4522L",   size: "Large",    unit_price: 189.00, case_price: 1134.00 },
      { sku: "4522XL",  size: "X-Large",  unit_price: 189.00, case_price: 1134.00 },
      { sku: "4522XXL", size: "2X-Large", unit_price: 189.00, case_price: 1134.00 },
    ],
    productUrl: "https://www.cordovasafety.com/product/4522/",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/gloves/cordova-4522-spec-sheet.pdf`,
    featured: true,
    badge: "New",
  },
  {
    id: "filmop-alpha",
    name: "Filmop Alpha EVS Workstation",
    category: "evs-janitorial",
    brand: "Filmop USA",
    tagline: "Customizable. Secure. Ergonomic.",
    description: "Fully customizable EVS workstation cart with lockable compartments, integrated charging, and ergonomic design. Perfect for healthcare environments.",
    image: "https://www.filmopusa.com/images/product-page-images/workstation-overview-top-950.gif",
    features: [
      "Lockable compartments",
      "Integrated charging stations",
      "Customizable configurations",
      "Ergonomic handles",
      "Heavy-duty construction",
      "Easy-clean surfaces"
    ],
    specs: {
      "Material": "High-impact polymer",
      "Weight Capacity": "200+ lbs",
      "Configurations": "50+ options"
    },
    applications: ["Patient room cleaning", "OR turnover", "Terminal cleaning"],
    productUrl: "https://www.filmopusa.com/products/product-workstation-overview.html",
    featured: true,
    badge: "Popular"
  },
  {
    id: "autonomous-scrubber",
    name: "Autonomous Floor Scrubber",
    category: "equipment",
    brand: "Minuteman",
    tagline: "24/7 Automated Cleaning",
    description: "Self-navigating floor scrubber for healthcare facilities. Programs routes, cleans on schedule, and generates compliance reports.",
    image: "https://sterilemate.com/wp-content/uploads/2025/11/Autonomous-Floor-Care.webp",
    features: [
      "Autonomous navigation",
      "Route programming",
      "Obstacle avoidance",
      "Compliance reporting",
      "Remote monitoring",
      "Auto-charging dock"
    ],
    specs: {
      "Cleaning Path": "20-26 inches",
      "Runtime": "Up to 6 hours",
      "Tank Capacity": "Varies by model"
    },
    applications: ["Corridor cleaning", "Night shift operations", "High-traffic areas"],
    productUrl: "https://sterilemate.com/autonomous-floor-care/",
    badge: "Smart"
  },
  {
    id: "hepa-vacuum",
    name: "Critical Filter HEPA Vacuum",
    category: "equipment",
    brand: "Minuteman",
    tagline: "Hospital-Grade Filtration",
    description: "Critical HEPA filtration vacuum for healthcare environments. Captures 99.97% of particles at 0.3 microns.",
    image: "https://sterilemate.com/wp-content/uploads/2025/11/Hospital-Vacuum-1.webp",
    features: [
      "HEPA filtration (99.97%)",
      "Hospital-grade design",
      "Low noise operation",
      "Ergonomic handling",
      "Easy bag change"
    ],
    specs: {
      "Filtration": "99.97% @ 0.3 microns",
      "Noise Level": "<62 dB",
      "Capacity": "6-15 gallons"
    },
    applications: ["Patient rooms", "OR suites", "Isolation rooms", "Pharmacy"],
    productUrl: "https://sterilemate.com/hospital-vacuums/"
  },
  {
    id: "microfiber-system",
    name: "Scrubbing Wet Flat Mop — Color-Coded Microfiber",
    category: "evs-janitorial",
    brand: "Sterile Mate",
    tagline: "Polypropylene-Ridge Scrubbing Microfiber for Heavy Grime",
    description: "Color-coded microfiber scrubbing wet flat mop for infection-control cleaning. Polypropylene ridges scrub heavy grime while microfiber lifts and holds soil. Double-stitched edges, up to 500 launder cycles per pad.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/microfiber/mop-scrubber-18.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/microfiber/mop-scrubber-18.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/microfiber/mop-scrubber-10.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/microfiber/mop-color-coded-wet.jpg`,
    ],
    features: [
      "Polypropylene ridges scrub heavy grime",
      "Color-coded (Blue, Green, Red) for infection control",
      "Double-stitched edges — 500+ launder cycles",
      "Three sizes: 13\", 18\", 24\"",
      "Reduces water changes — higher square-footage per fill",
      "Fits standard flat-mop frames"
    ],
    specs: {
      "Sizes": "13\", 18\", 24\"",
      "Colors": "Blue, Green, Red (color-coded EVS)",
      "Material": "Split microfiber + polypropylene scrub ridges",
      "Wash Cycles": "500+",
      "13\" SKU examples": "M830013B (Blue), M830013G (Green)",
      "18\" SKU examples": "M830018B / M830018G / M830018R",
      "24\" SKU examples": "M830024B / M830024G / M830024R",
      "Case": "10 DOZ / CASE"
    },
    applications: ["Patient-room turns", "SPD floors", "Discharge cleaning", "Heavy-grime dietary floors", "Color-coded EVS programs"],
    productUrl: "https://sterilemate.com/product/scrubbing-wet-flat-mop/"
  },
  {
    id: "dilution-control",
    name: "Chemical Dilution Control System",
    category: "consumables",
    brand: "MultiClean",
    tagline: "Precise. Consistent. Cost-Effective.",
    description: "Automated chemical dilution system ensuring precise concentrations every time. Reduces waste and ensures effectiveness.",
    image: "https://sterilemate.com/wp-content/uploads/2025/11/128-E-Fecticide-2L-1.webp",
    features: [
      "Precise dilution ratios",
      "Multiple product dispensing",
      "Wall-mounted design",
      "Easy refill system",
      "Usage tracking"
    ],
    specs: {
      "Stations": "4-8 products",
      "Accuracy": "±5%",
      "Flow Rate": "1 GPM"
    },
    applications: ["EVS departments", "Central supply", "Satellite stations"],
    productUrl: "https://sterilemate.com/dilution-control/"
  },
  {
    id: "green-seal-mops",
    name: "Green Seal GS-20 Certified Mop Heads",
    category: "consumables",
    brand: "ABCO Products",
    tagline: "First Certified Green Mop",
    description: "First and only Green Seal GS-20 certified mop heads. Environmentally responsible without compromising performance.",
    image: "https://sterilemate.com/wp-content/uploads/2025/11/Bio-Power-1.webp",
    features: [
      "Green Seal GS-20 certified",
      "UL Environmental Validated",
      "High absorbency",
      "Durable construction",
      "Multiple sizes"
    ],
    specs: {
      "Certification": "Green Seal GS-20",
      "Material": "Recycled cotton blend",
      "Sizes": "16oz - 32oz"
    },
    applications: ["General mopping", "Spill cleanup", "Restroom cleaning"],
    productUrl: "https://abcoproducts.com/",
    badge: "Eco-Friendly"
  },
  // ============================================================
  // Home Health Care Aids — Vive Health (Vive Precision)
  // Authorized distribution — pricing reflects Sterile Mate sell.
  // ============================================================
  {
    id: "vive-dmd1087wht-bp-monitor",
    name: "Standard Blood Pressure Monitor",
    category: "home-health-aids",
    brand: "Vive Precision",
    tagline: "Clinical-grade BP + heart rate · One-touch operation",
    description: "Compact upper-arm BP monitor with irregular-heartbeat detection, 198-reading memory, and an adjustable cuff that fits 8.66\"–16.53\" arms. Includes 4× AAA batteries — ready to use out of the box.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1087wht-bp-monitor.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1087wht-bp-monitor.jpg`,
    ],
    features: [
      "One-touch operation — senior-friendly",
      "Irregular-heartbeat detection",
      "198-reading memory with date/time",
      "Adjustable cuff (8.66\" – 16.53\")",
      "Color-coded BP indicator on LCD",
      "60-day Vive guarantee",
    ],
    specs: {
      "SKU": "DMD1087WHT",
      "Manufacturer": "Vive Health (Vive Precision)",
      "Dimensions": "188 × 98 × 62.5 mm (4.65 × 3.86 × 2.46 in)",
      "Weight": "Monitor 0.57 lb · Cuff 0.24 lb",
      "Power": "4× AAA batteries (included)",
      "UPC": "810113574905",
    },
    applications: ["Home health programs", "Patient discharge kits", "Clinic vital-signs stations"],
    customer_price: 25.56,
    productUrl: "https://www.vivehealth.com/products/standard-bpm",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/specs/dmd1087wht-bp-monitor-manual.pdf`,
    featured: true,
    badge: "New"
  },
  // ============================================================
  // Glucose Monitors — Diabetes Management — Vive Health (Vive Precision)
  // Home Health Care Aids > Glucose Monitors. No pricing published —
  // quoted per program volume through the Sterile Mate quote request.
  // ============================================================
  {
    id: "vive-dmd1079wht-glucose-monitor-kit",
    name: "Blood Glucose Monitoring System Kit",
    category: "home-health-aids",
    subcategory: "Glucose Monitors",
    brand: "Vive Precision",
    tagline: "Diabetes management · Result in 5 seconds · No-code strips",
    description: "Complete out-of-the-box blood glucose monitoring kit for home and clinic diabetes programs. No-code test strips auto-absorb a 0.7 µL sample and return a reading in 5 seconds, with 500-reading memory stamped with date and time. Includes the meter, a 5-level adjustable lancing device, 25 test strips, 25 lancets, a carrying case and batteries — ready to hand to a patient at discharge.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-glucose-kit.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-glucose-kit.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-glucose-monitor.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-glucose-accuracy.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-lancing-device.jpg`,
    ],
    features: [
      "Blood glucose reading in 5 seconds",
      "No-code, pre-calibrated test strips — no setup error",
      "500-reading memory with date and time stamps",
      "5-level adjustable-depth lancing device for less pain",
      "Pairs with the Vive Precision app for patient tracking",
      "Ready for immediate use — strips, lancets and batteries included",
    ],
    specs: {
      "SKU": "DMD1079WHT",
      "Manufacturer": "Vive Health (Vive Precision)",
      "Result Time": "5 seconds",
      "Sample Size": "0.7 µL",
      "Memory": "500 readings with date/time",
      "Dimensions": "90 × 54 × 13 mm",
      "Weight": "73 g (excluding batteries)",
      "Power": "2× AAA alkaline batteries (included)",
      "Strip Storage": "39 °F – 86 °F, humidity < 85% RH",
      "Cleaning": "CaviWipes",
    },
    applications: ["Diabetes management programs", "Patient discharge kits", "Remote patient monitoring", "Clinic point-of-care testing"],
    productUrl: "https://www.vivehealth.com/products/blood-glucose-monitoring-system",
    featured: true,
    badge: "New"
  },
  {
    id: "vive-dmd1082-glucose-test-strips",
    name: "Blood Glucose Test Strips — 50 ct / 100 ct",
    category: "home-health-aids",
    subcategory: "Glucose Monitors",
    brand: "Vive Precision",
    tagline: "No-code · Auto-absorbing · 50 or 100 count",
    description: "Replacement test strips for the Vive Precision blood glucose monitoring system. Pre-calibrated no-code strips auto-absorb a 0.7 µL sample and deliver a result in 5 seconds. Packed 50 count (2 × 25) or 100 count (2 × 50) so a program can standardize its refill cadence instead of reordering ad hoc.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-glucose-accuracy.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-glucose-accuracy.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-glucose-kit.jpg`,
    ],
    features: [
      "No-code, pre-calibrated for consistent accuracy",
      "0.7 µL sample — smaller stick for the patient",
      "Result in 5 seconds",
      "50 ct (2 × 25) and 100 ct (2 × 50) configurations",
      "Matched to the DMD1079WHT meter",
    ],
    specs: {
      "SKU": "DMD1082BLU50CT · DMD1082BLU100CT",
      "Manufacturer": "Vive Health (Vive Precision)",
      "Compatible Meter": "DMD1079WHT",
      "Result Time": "5 seconds",
      "Sample Size": "0.7 µL",
      "Pack Sizes": "50 count (2 × 25) · 100 count (2 × 50)",
      "Storage": "39 °F – 86 °F, humidity < 85% RH",
    },
    applications: ["Diabetes management programs", "Standing refill orders", "Pharmacy DME programs", "Home health agencies"],
    productUrl: "https://www.vivehealth.com/products/blood-glucose-test-strips",
    badge: "Refill"
  },
  {
    id: "vive-easy-twist-lancets",
    name: "Easy-Twist Lancets & 5-Level Lancing Device",
    category: "home-health-aids",
    subcategory: "Glucose Monitors",
    brand: "Vive Precision",
    tagline: "Twist-off cap · 5 depth levels · Single-use sterile",
    description: "Sterile single-use twist-top lancets and the adjustable lancing device they pair with. Five depth settings let the stick be matched to the patient's skin thickness instead of defaulting to the deepest setting, which is the single biggest reason patients quit testing.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-lancing-device.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-lancing-device.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1079wht-glucose-kit.jpg`,
    ],
    features: [
      "Twist-off protective cap — no exposed tip before use",
      "5 adjustable depth levels on the lancing device",
      "Sterile, single-use",
      "Universal fit with the Vive lancing device",
      "Bulk refill counts for program volumes",
    ],
    specs: {
      "SKU": "Vive Easy-Twist Lancets",
      "Manufacturer": "Vive Health (Vive Precision)",
      "Type": "Single-use twist-top lancet",
      "Device Depth Settings": "5 levels",
      "Sterilization": "Sterile · single-use",
      "Disposal": "Sharps container required",
    },
    applications: ["Diabetes management programs", "Discharge kit refills", "Clinic point-of-care testing", "Home health agencies"],
    productUrl: "https://www.vivehealth.com/collections/vive-precision",
    badge: "Refill"
  },
  // ============================================================
  // Blood Pressure Monitors — Sterile Mate (InBody BPBIO series)
  // Pricing to be added later (net + retail via admin CSV upload)
  // ============================================================
  {
    id: "bpbio-220",
    name: "BPBIO 220 — Mercury-Free Manual Blood Pressure Monitor",
    category: "blood-pressure-monitors",
    subcategory: "Blood Pressure Monitors",
    brand: "Sterile Mate",
    tagline: "Clinically validated · Auscultatory · One-hand controller",
    description: "Manual mercury-free blood pressure monitor built for hospitals and clinics. Uses the auscultatory method with a large LCD, smart backlight, and a patented one-hand controller for marking readings and controlling exhaust speed.",
    image: "https://sterilemate.com/wp-content/uploads/2023/05/BPBIO220_front_side_with-cuff_TP.jpg",
    gallery: [
      "https://sterilemate.com/wp-content/uploads/2023/05/BPBIO220_front_side_with-cuff_TP.jpg",
      "https://sterilemate.com/wp-content/uploads/2023/05/BPBIO220_large-display-1.jpg",
      "https://sterilemate.com/wp-content/uploads/2023/05/Device_backlight-1.jpg",
      "https://sterilemate.com/wp-content/uploads/2023/05/BPBIO220_Controller-1-1.jpg",
    ],
    features: [
      "Large LCD — systolic, diastolic, and pulse rate at a glance",
      "Smart backlight — auto-adjusts to ambient brightness",
      "One-hand controller — mark BP readings + control exhaust speed",
      "Pulse measurement — auto-displayed after every BP test",
      "Compact storage — cuff & air-bulb tuck into the device back",
      "Angle-control LCD — pivots to user eye level",
      "Emergency detection — auto-exhaust above 320 mmHg",
      "Auto power-off after 5 min idle",
    ],
    specs: {
      "SKU": "BPBIO-220",
      "Manufacturer": "Sterile Mate (InBody)",
      "Type": "Manual auscultatory blood pressure monitor",
      "Measurement Method": "Auscultatory (mercury-free)",
      "Safety": "Rapid exhaust triggered above 320 mmHg",
    },
    applications: ["Hospital exam rooms", "Outpatient clinics", "Medical education & training"],
    productUrl: "https://sterilemate.com/product/bpbio-220/",
    badge: "Manual"
  },
  {
    id: "bpbio-250",
    name: "BPBIO 250 — Fully Automated Clinical Blood Pressure Monitor",
    category: "blood-pressure-monitors",
    subcategory: "Blood Pressure Monitors",
    brand: "Sterile Mate",
    tagline: "Oscillometric · One-touch cuff · Bluetooth · 5 modes",
    description: "Clinically validated, fully automated blood pressure monitor designed for hospitals and medical clinics. Features a patented one-touch auto-curling cuff, five selectable measurement modes, oscillometric-method accuracy, Bluetooth, and a rechargeable lithium-ion battery.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio250.png`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio250.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio250-feature.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio250-touch1.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio250-touch2.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio250-touch3.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio250-touch4.jpg`,
    ],
    features: [
      "Patented one-touch cuff — easily wraps around the patient's upper arm",
      "Five measurement modes (NORMAL · AUSCULT · CYCLE · CYCLE RANGE · CYCLE HIDE)",
      "Oscillometric method · ±3 mmHg pressure, ±3% pulse accuracy",
      "Backlight LCD — easy to read in low-light or night-shift environments",
      "Rechargeable lithium-ion battery — ~500 measurements per full charge",
      "Bluetooth connectivity for data sync",
      "Cuff rack + curl cable for tidy bedside storage",
      "Cuff sizes: S 17–22 cm · M 22–32 cm · L 32–42 cm",
      "Optional 5-wheel mobile trolley stand",
    ],
    specs: {
      "SKU": "BPBIO-250",
      "Manufacturer": "InBody (Sterile Mate authorized dealer)",
      "Measurement Method": "Oscillometric",
      "Measurement Range": "Pressure 0–300 mmHg · Pulse 30–240 bpm",
      "Accuracy": "Pressure ±3 mmHg · Pulse ±3%",
      "Measurement Time": "~30 seconds average",
      "Dimensions": "122(W) × 150(H) × 195(L) mm",
      "Weight": "~1,105 g",
      "Display": "Digital custom segment LCD (backlit)",
      "Power": "AC adapter · Li-Ion (2600 mAh) · 4×AA",
      "Connectivity": "Bluetooth 4.0 LE",
      "Memory": "99 readings",
    },
    applications: ["Hospital wards", "Outpatient clinics", "Ambulatory care", "Cardiology practices"],
    productUrl: "https://inbodyusa.com/products/bpbio-250/",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/downloads/brochures/inbody-bpbio-320-catalog.pdf`,
    featured: true,
    badge: "Automated"
  },
  {
    id: "bpbio-320s",
    name: "BPBIO 320S — Self-Testing Blood Pressure Kiosk",
    category: "blood-pressure-monitors",
    subcategory: "Blood Pressure Monitors",
    brand: "Sterile Mate",
    tagline: "Contact-free self-test · Voice guidance · ~30-second result",
    description: "Clinically validated fully-automatic blood pressure kiosk (ESH IP2 protocol). Inflationary-oscillometric method delivers pain-free measurement, with voice guidance, elbow-position sensor, motion sensor, one-touch start, high-speed thermal printer, and emergency stop button. Optional disposable cuff barrier for sanitary contact-free testing. Ideal for hospitals, clinics, and pharmacies.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio320.png`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio320.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio320-feature.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio320-tech2.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio750-tech1.jpg`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio750-feature1.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/inbody/bpbio750-point.jpg`,
    ],
    features: [
      "Voice guidance — hands-free step-by-step self-measurement",
      "Elbow sensor auto-detects proper arm positioning",
      "Motion sensor detects human presence (BPBIO 320S only)",
      "Inflationary oscillometric method — reduces pain while measuring",
      "One-button start · results in ~30 seconds",
      "High-speed thermal printer with customizable result sheet",
      "Two Start/Stop buttons (front & back) + Emergency Stop",
      "Clinically validated per ESH protocol (IP2)",
      "Applicable arm circumference: 17–42 cm",
      "Optional disposable cuff barrier for contact-free hygiene",
      "Interfaces with InBody 270/370S/570/770 for combined result sheets",
    ],
    specs: {
      "SKU": "BPBIO-320S",
      "Manufacturer": "InBody (Sterile Mate authorized dealer)",
      "Measurement Method": "Inflationary Oscillometric",
      "Measurement Range": "Pressure 0–300 mmHg · Pulse 30–240 bpm",
      "Accuracy": "Pressure ±2 mmHg · Pulse ±1.5%",
      "Measurement Time": "~30 seconds average (20–50 s)",
      "Applicable Arm Circumference": "17–42 cm",
      "Display": "7-Segment LED",
      "Printer": "High-speed thermal",
      "Dimensions": "489(W) × 450(L) × 284(H) mm",
      "Weight": "~9 kg",
      "External Interface": "RS232C (USB via dedicated cable)",
      "Power": "AC 100–240V, 50/60Hz",
      "Optional Items": "Dedicated desk · height-adjustable chair",
    },
    applications: ["Hospital lobbies", "Pharmacies", "Clinics", "Community health screenings", "Corporate wellness"],
    productUrl: "https://inbodyusa.com/products/bpbio-320s/",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/downloads/brochures/inbody-bpbio-320-catalog.pdf`,
    featured: true,
    badge: "Kiosk"
  },
  // ============================================================
  // Diabetes Care — Sterile Mate (9 SKUs)
  // Home Health Care Aids > Diabetes Care
  // ============================================================
  ...([
    ["sol-care-safety-heel-blood-lancets-2", "Sol-Care Safety Heel Blood Lancets", "https://sterilemate.com/wp-content/uploads/2023/12/Heel-Blood-Lancets-1-300x300.jpg", "Sol-Care", "Single-use safety heel lancets for capillary blood glucose sampling."],
    ["sol-care-safety-insulin-syringe", "Sol-Care Safety Insulin Syringe", "https://sterilemate.com/wp-content/uploads/2023/12/Safety-Insulin-Syringe-7-300x300.jpg", "Sol-Care", "Safety-engineered insulin syringe with retractable shield."],
    ["sol-care-safety-lancets-2", "Sol-Care Safety Lancets", "https://sterilemate.com/wp-content/uploads/2023/12/Safety-Lancets-4-300x300.jpg", "Sol-Care", "Push-button retractable safety lancets for glucose monitoring."],
    ["sol-guard-safety-insulin-syringe", "Sol-Guard Safety Insulin Syringe", "https://sterilemate.com/wp-content/uploads/2023/12/64f1e96e41906b7deea683ab_62e907163edd9b185bcb0092_SIA1001_01-300x300.jpg", "Sol-Guard", "Sol-Guard low-dead-space safety insulin syringe."],
    ["sol-m-alcohol-swabs-2", "Sol-M Alcohol Swabs", "https://sterilemate.com/wp-content/uploads/2023/12/Alcohol-Swabs-4-300x300.jpg", "Sol-M", "70 percent isopropyl alcohol prep pads for diabetic-testing hygiene."],
    ["sol-m-blood-lancet", "Sol-M Blood Lancet", "https://sterilemate.com/wp-content/uploads/2023/12/Blood-Lancent-3-300x300.jpg", "Sol-M", "Sol-M single-use blood lancet for capillary glucose sampling."],
    ["sol-m-lancing-device-2", "Sol-M Lancing Device", "https://sterilemate.com/wp-content/uploads/2023/12/Lancing-Device-1-300x300.jpg", "Sol-M", "Reusable adjustable-depth lancing device for diabetic testing."],
    ["sol-m-standard-insulin-syringe", "Sol-M Standard Insulin Syringe", "https://sterilemate.com/wp-content/uploads/2023/12/Insulin-Syringe-5-300x300.jpg", "Sol-M", "Standard non-safety insulin syringe with attached needle."],
    ["sol-m-standard-pen-needle", "Sol-M Standard Pen Needle", "https://sterilemate.com/wp-content/uploads/2023/12/Standar-Pen-Needle-1-300x300.jpg", "Sol-M", "Universal-fit insulin pen needle, single-use sterile."],
  ].map(([slug, name, image, brand, description]) => ({
    id: slug,
    name,
    category: "home-health-aids",
    subcategory: "Diabetes Care",
    brand,
    tagline: "Diabetes Care · Safety-engineered · Single-use",
    description,
    image,
    gallery: [image],
    features: ["Sterile single-use packaging", "Safety-engineered where applicable", "OSHA needle-stick compliant"],
    specs: { "SKU": slug.toUpperCase(), "Manufacturer": brand, "Line": "Diabetes Care", "Sterilization": "Sterile · single-use" },
    applications: ["Diabetic testing at home", "Clinic-based glucose monitoring", "Pharmacy DME programs"],
    productUrl: `https://sterilemate.com/product/${slug}/`,
    badge: "Diabetes Care"
  }))),
  // ============================================================
  // ============================================================
  // Microfiber Cleaning — Direct Mop Sales (70 SKUs)
  // Under Janitorial & EVS, subcategory "Microfiber"
  // Monarch Brands closed Q4 2025. Product lineup + imagery now sourced from
  // Direct Mop Sales, Inc. (directmopsales.com) — an active mop/microfiber
  // manufacturer/distributor. Product photos are hot-linked from their live
  // CDN so they refresh whenever the vendor updates them.
  // ============================================================
  ...([
    // ── Microfiber Mop Pads (17) ────────────────────────────────
    ["mf-wet-mop-pad-standard-loop", "Standard Loop Pile Microfiber Hook and Loop Wet/Dry Mopping Pads", "microfiber-mop-pad-main.jpg?v=1752417489", "microfiber-wet-mop-pad", 2.90, "Standard loop-pile microfiber wet/dry mopping pad — hook-and-loop backing fits any compatible frame. Launderable, color-coded, workhorse SKU for terminal-room cleaning."],
    ["mf-economy-mop-pad-18", "18\" Economy Standard Looped Pile — Microfiber Wet/Dry Pad", "economy-microfiber-mop-pad-single-category_8f73cba6-eb0e-44fc-8c98-29f5b97630b9.jpg?v=1752417552", "economy-microfiber-mop-pad", 2.55, "Economy 18\" looped-pile wet/dry pad — hook-and-loop backing, budget-friendly for high-volume EVS routes."],
    ["mf-healthcare-mop-pad", "Healthcare Microfiber Hook and Loop Pads", "microfiber-mop-pad-main.jpg?v=1752417489", "healthcare-microfiber-mop", 3.80, "Healthcare-grade microfiber hook-and-loop pad engineered for hospital terminal-clean protocols. Bleach-safe binding, color-coded."],
    ["mf-twisted-closed-loop-pad", "Microfiber Closed-loop Deep Clean Hook and Loop Pad", "Microfiber-Mop-Pads-Twisted-Loop-main.jpg?v=1752417522", "microfiber-twisted-closed-loop-mop-pads", 3.70, "Twisted closed-loop microfiber pad — deep-clean scrubbing plus soft glide. Grey w/ red binding for aggressive soil pickup."],
    ["mf-scrubber-pad-gray", "Gray Microfiber Scrubber Hook and Loop Mopping Pad", "Microfiber-Mop-Pads-Scrubber-Pad-category_9cbc6dfc-c7a7-40c7-bd86-2a4d042bcb5b.jpg?v=1752417578", "microfiber-scrub-pad", 3.60, "Gray microfiber scrubber pad with abrasive stripes for baked-on grime, grout lines, and heavy-soil clean-ups."],
    ["mf-dust-mop-pad-fringe", "Microfiber Hook and Loop Dust Mop with Fringe Yarn", "Microfiber-Mop-Pads-Dust-Mop-category.jpg?v=1708692285", "microfiber-dust-mop-pad", 3.45, "Fringe-yarn dust-mop pad — traps particulate on hard floors, gymnasiums, and long corridors. Hook-and-loop backing."],
    ["mf-color-coded-scrubber-pad", "Cut-Pile Scrubber Microfiber Hook and Loop Mopping Pad", "Microfiber-Mop-Pad-Color-Coded-Scrubber-All-Colors_18a434e9-54c3-40f1-b4f2-f5ee3ddbf64a.jpg?v=1752417640", "microfiber-mop-color-coded", 2.95, "Cut-pile scrubber pad in five colors for HAI-zoning workflows. Standard hook-and-loop backing."],
    ["mf-cut-pile-wet-dry-pad", "Cut-Pile Microfiber Hook and Loop Wet/Dry Mopping Pad", "microfiber-mop-pad-cut-pile.jpg?v=1726662099", "microfiber-hook-and-loop-mop", 2.40, "Cut-pile wet/dry mopping pad — value SKU for daily EVS. Absorbent, launderable, hook-and-loop."],
    ["mf-disposable-mop-pad-240", "Economy Disposable Microfiber Pads (Bulk 240/Case)", "Microfiber-Mop-Pads-Disposable-Bulk-240.jpg?v=1717703524", "disposable-microfiber-mop-pad", 85.00, "240/case disposable microfiber mop pads for isolation rooms and C-diff protocols. One-use, no laundering."],
    ["mf-finish-applicator-pad", "Microfiber Finish Applicator Hook and Loop Pad", "Microfiber-mop-pads-finish-pad-category.jpg?v=1708692339", "microfiber-finish-mop-pad", 4.10, "Lint-free finish applicator pad for floor wax, sealer, and polymer application. Hook-and-loop compatible."],
    ["mf-true-finish-pad-18", "True 18\" Microfiber Finish Pad — Gray with Red Binding", "microfiber-finish-pads-true-fit.jpg?v=1722429185", "true-microfiber-finish-pad", 3.25, "True-fit 18\" finish pad — gray body with red binding. Streak-free wax and polymer application."],
    ["mf-frame-10in-aluminum", "10\" Microfiber Aluminum Channel Frame with Hook and Loop", "microfiber_mop_frame_4a3caae9-650c-4fb1-8339-aa01c713ccb3.jpg?v=1708524414", "products-10in-aluminum-microfiber-mop-frame-channel", 8.00, "10\" aluminum channel frame with hook-and-loop backing. Tight quarters, wall-washing, and detail work."],
    ["mf-wall-wash-pad-10", "10\" Standard Loop Pile Microfiber Hook and Loop Wall Washing Pad", "BULKMFP10B-2_8caa4fb9-5bf8-40ba-b257-194ced39726d.jpg?v=1780156411", "10in-microfiber-wall-washing-pad-hook-loop", 2.20, "10\" wall-washing pad, standard loop pile. Pairs with 10\" aluminum frame + telescopic pole."],
    ["mf-cut-pile-pad-10-blue", "10\" Blue Cut-Pile Microfiber Hook and Loop Mop Pad", "microfiber-mop-pad-wall-washing-cut-pile_c566f065-38da-4952-8160-50578eb9c9a9.jpg?v=1780156683", "10in-blue-cut-pile-microfiber-mop-pad", 1.95, "10\" cut-pile mop pad in blue. Compact detail pad for wall-washing, baseboards, and reach-behinds."],
    ["mf-fringe-dust-pad-12", "12\" Dust Mop with Fringe Yarn — Microfiber Hook and Loop Pad", "MFR10BF-2T_2cc00f24-3070-4c5e-8610-d548711279af.jpg?v=1780158239", "12in-fringe-yarn-microfiber-dust-mop-pad", 2.80, "12\" fringe-yarn dust pad — pick-up power for narrow corridors and detail dry-mop routes."],
    ["mf-hand-trowel-9", "9\" x 3¾\" Blue Microfiber Hand Trowel — Hook and Loop", "BULKTROWELB-2T_91bb9ea3-00c2-42ae-8f23-a559d70244c6.jpg?v=1780158465", "9in-handheld-microfiber-trowel-hook-loop", 5.75, "9\" handheld trowel-style microfiber mop for stairs, treads, radiators, and above-fixture cleaning."],
    ["mf-plastic-frame-9", "Plastic Microfiber Frame — 9\" x 3¾\" Blue with Hook and Loop", "BULKPMFRB9-2T_81c92081-afe6-404f-8d3d-6bbc1a15daf1.jpg?v=1780158591", "products-9in-plastic-microfiber-frame-hook-loop", 5.90, "9\" x 3¾\" plastic frame with hook-and-loop backing. Pairs with 9\" hand trowel pads."],

    // ── Dust Mops (6) ───────────────────────────────────────────
    ["mf-industrial-twisted-dust-mop", "Dust Mops — Twisted Closed-Loop Industrial Grade — Launderable", "dust-mop-industrial-twisted-loop-all-colors.jpg?v=1730316166", "dust-mop", 4.75, "Twisted closed-loop industrial dust mop — launderable, long-fiber pick-up for warehouses, gymnasiums, and back-of-house."],
    ["mf-dust-mop-kit-industrial", "Dust Mop Complete Kit — Twisted Closed-Loop Industrial Grade", "dust-mop-kit-main.jpg?v=1733404854", "dust-mop-kit-industrial", 22.60, "Complete industrial dust-mop kit — head, backer frame, and handle bundled for out-of-the-box deployment."],
    ["mf-industrial-microfiber-dust-mop", "Industrial Microfiber Dust Mop — Closed-Loop", "microfiber-dust-mops-category-picture.jpg?v=1730291951", "industrial-microfiber-dust-mop", 5.30, "Closed-loop industrial microfiber dust mop — traps micro-particulate that traditional cotton misses."],
    ["mf-blue-industrial-dust-mop", "Blue Industrial Microfiber Dust Mop", "Microfiber-Dust-Mops-Blue-traditional-36-inch-main.jpg?v=1729001811", "microfiber-dry-mop", 7.60, "Blue traditional 36\" industrial microfiber dust mop — long-corridor sweeping and back-of-house dry mopping."],
    ["mf-cotton-yarn-disposable-dust-mop", "Cotton Yarn Disposable Dust Mops", "disposable-dust-mop-heads-category_0ea7d510-7e30-4e9c-9676-6a680736e89e.jpg?v=1708718193", "disposable-dust-mop", 3.70, "Single-use cotton-yarn disposable dust mop. Ideal for construction cleanup and one-time turnovers."],
    ["mf-wedge-dust-mop", "Wedge Dust Mops", "DustMops-Wedge.jpg?v=1708092710", "wedge-dust-mops", 2.60, "Wedge-shaped dust mop for corners, tight aisles, and under-fixture reach. Multiple color codes."],

    // ── Microfiber Cloths (12) ──────────────────────────────────
    ["mf-cloth-terry-16-300", "Microfiber Cleaning Cloths — Terry 16\" x 16\" — 300gsm", "microfiber-terry-cloths-group-picture_9f5afb99-c405-409a-a6bb-f47192502796.jpg?v=1708689977", "16-x-16-300-terry-microfiber-cleaning-cloths", 9.15, "Professional-quality 300gsm terry microfiber cloth in 12+ colors. High absorption, dye-safe, 300+ launder cycles."],
    ["mf-cloth-terry-16-400", "Microfiber Cleaning Cloths — Terry 16\" x 16\" — 400gsm Performance", "microfiber-cloths-economy_ec9bf3dd-df92-481e-9539-0bd8bff63c06.jpg?v=1708690114", "microfiber-cloths-performance", 12.60, "Performance-grade 400gsm terry microfiber cloth — extra plush, more absorbent, top choice for OR turnovers."],
    ["mf-cloth-terry-16-200", "Microfiber Cleaning Cloths — Terry 16\" x 16\" — 200gsm Standard", "microfiber-cloths-economy_c1a2e35e-ed22-4984-b573-cadf1e1492b0.jpg?v=1708690010", "microfiber-terry-cloths-light-weight-16-16-200", 6.00, "Standard 200gsm terry microfiber cloth — daily EVS workhorse for glass, chrome, and general dust routes."],
    ["mf-cloth-terry-12-300", "Microfiber Cleaning Cloths — Terry 12\" x 12\" — 300gsm Professional", "microfiber-cloths-economy_b5e7e22b-ea37-45ca-a79d-84257e6c7fb9.jpg?v=1708690089", "microfiber-cloths-12x12-professional", 7.05, "Professional 300gsm terry cloth in a 12x12 detail size — fits standard glove-holder dispensers, pocketable."],
    ["mf-cloth-terry-12-200", "Microfiber Cleaning Cloths — Terry 12\" x 12\" — 200gsm Standard", "microfiber-cloths-economy_1c1b34cd-d350-408a-89d0-3d347a369ab7.jpg?v=1708690055", "microfiber-cloths-terry-12x12-standard", 4.35, "Standard 200gsm 12x12 terry microfiber. Everyday dust and wipe-down workhorse."],
    ["mf-textured-glass-cloth", "Microfiber Textured Glass Cloths — 16\" x 16\"", "microfiber-textured-glass-cloths-main.jpg?v=1752421225", "microfiber-textured-glass-cleaning-cloths", 12.85, "Textured microfiber glass cloth — streak-free glass, chrome, and stainless. Non-absorbent for wipe-and-dry work."],
    ["mf-honeycomb-glass-cloth", "Microfiber Glass Cloths — Honeycomb — 16\" x 16\"", "compressed-waffle-weave-microfiber-cloths-main.jpg?v=1752421267", "microfiber-cloths-honey-comb", 15.75, "Honeycomb-weave microfiber glass cloth. Grabs mineral spots on windows and mirrors, no lint."],
    ["mf-smooth-glass-cloth", "Microfiber Smooth Glass Cleaning Cloths — 16\" x 16\"", "SMOOTHGLASSCLOTHS5-main.jpg?v=1752421288", "microfiber-glass-cleaning-cloth", 10.70, "Smooth-weave microfiber glass cloth — soft, lint-free finishing wipe for mirrors and stainless."],
    ["mf-suede-cloth-gray", "Microfiber Suede Cloths — 16\" x 16\" — Gray", "Microfiber-Cloths-Suede-Gray-main.jpg?v=1752421316", "suede-microfiber-cloths", 13.85, "Suede-finish microfiber cloth in gray — professional detail-and-buff work on painted, chrome, and glass surfaces."],
    ["mf-combination-scrubber-cloth", "Microfiber Combination Scrubber Cloths — 12\" x 12\"", "Microfiber-Cloths-Scrubber-main.jpg?v=1752421352", "microfiber-cloths-combination", 12.85, "Combination scrubber cloth — microfiber on one side + non-scratch scrubbing mesh on the other. Two tools in one."],
    ["mf-mesh-scrub-cloth", "Microfiber Mesh Scrub Cleaning Cloths", "MICROFIBERSCRUBCLOTH-2_af013ab0-90ce-489a-b21a-aa03e1ed7d41.jpg?v=1707228117", "microfiber-scrub-cloths", 7.75, "Mesh-scrub microfiber cloth — aggressive dirt pick-up for restrooms and back-of-house detail work."],
    ["mf-fbg-branded-cloth", "FBG BRANDED Microfiber Cleaning Cloths", "microfiber-cloth-16-300-green-2_e6949eea-c45c-49e3-a68c-70cf4cd2593c.jpg?v=1715008216", "fbg-branded-microfiber-cleaning-cloths", 9.55, "FBG-branded 300gsm 16x16 terry microfiber cloth in bulk case. Everyday universal EVS wipe."],
    ["mf-pop-n-pull-edgeless-270", "270 Ct. Case 12\"x12\" Pop N' Pull Edgeless Microfiber Wipes", "https://maximmart.com/images/detailed/3/IMG_20260306_015809_373_7pnz-gu.jpg", "pop-n-pull-edgeless-wipes", 84.96, "9-box case (270 wipes) of 12x12\" edgeless microfiber wipes with a pop-and-pull dispenser box. 80% polyester / 20% polyamide · 250x launderable · Light Blue. Perfect quick-use daily cloth for EVS, isolation, and turnaround protocols."],

    // ── Microfiber Dusters + Handles (4 dusters + 6 handles = 10) ──
    ["mf-hi-duster-sleeve-wand", "Microfiber Hi-Duster Sleeve/Wands", "Dusters-Dusting-Supplies-main-image_af91774c-7e33-42af-80f2-58e42bbc6f32.jpg?v=1752159460", "microfiber-hi-duster-sleeves-flexible-wand-ceiling-fan-duster", 3.00, "Flexible-wand microfiber duster sleeve — ceiling fans, tops of cabinets, above-fixture dust captures."],
    ["mf-compact-duster", "Compact Microfiber Dusters", "Microfiber-Dusters-small-main-image_09f054d1-2427-4855-8dd7-a79645431eaf.jpg?v=1752164288", "compact-microfiber-dusters-flexible-wand-cracks-crevices-wholesale", 2.35, "Compact microfiber duster — flexible wand for cracks, crevices, blinds, and detail dusting."],
    ["mf-static-duster-28-40", "28\" – 40\" Microfiber Static Duster — Assorted Colors", "Microfiber-Static-Dusters_9d8d37a2-abf1-4b14-bbda-b85378e189a8.jpg?v=1748008551", "microfiber-static-duster", 2.35, "Extendable 28\"-40\" microfiber static duster — attracts and holds dust electrostatically. Assorted colors."],
    ["mf-cleaning-mitt", "Microfiber Cleaning Hand Mitts", "Cleaning-Mitt-Microfiber-Dusters-main-image_7678611a-5577-4299-ba8a-e8fd724549b1.jpg?v=1748008725", "microfiber-cleaning-mitts", 2.05, "Microfiber cleaning hand-mitt — 5-finger dust and wipe. Machine-launderable, color-coded."],
    ["mf-static-duster-cover", "Microfiber Static Dust Cover", "MICROFIBERSTATICDUSTINGSLEEVES_6c346504-3141-4fbd-8fc3-0369ed9afb62.jpg?v=1748008878", "microfiber-static-duster-covers", 1.35, "Refill static-dust cover for wand duster — replace instead of laundering. Bulk case ships fast."],
    ["mf-handle-tele-72-aluminum", "Telescopic Handle — 72\" Aluminum", "mhdl72_a894b75b-345a-4d3c-aea9-8fd0c3506a8f.jpg?v=1708019418", "72-inch-telescopic-aluminum-handle", 10.80, "72\" aluminum telescopic handle — snaps into any hook-and-loop or channel frame. Grip-locked collar."],
    ["mf-handle-solid-59-aluminum", "Solid Aluminum Handle with Threaded End — 59\"", "mstr59.jpg?v=1752096331", "59-inch-solid-aluminum-handle-threaded-end", 9.30, "59\" solid aluminum handle with threaded end — pairs with threaded frames and dusters."],
    ["mf-handle-tele-8ft-2piece", "Telescopic Handle — 8-foot Aluminum, 2-Piece", "me28hdl.jpg?v=1752096546", "8-foot-telescopic-aluminum-handle-2-piece", 17.40, "8-foot 2-piece telescopic aluminum handle — high-reach ceiling and vent work."],
    ["mf-handle-tele-14ft-3piece", "Telescopic Handle — 14-foot Aluminum, 3-Piece", "me314hdl.jpg?v=1752096743", "14-foot-telescopic-aluminum-handle-3-piece", 29.20, "14-foot 3-piece telescopic aluminum handle — atrium and high-bay dusting."],
    ["mf-handle-tele-72-threaded", "Telescopic Handle — 72\" Aluminum, Threaded End", "MHDLTHD-2_58a0a373-4056-4e30-9d1b-8b3592cb16e8.jpg?v=1752410106", "72-inch-telescopic-aluminum-handle-threaded-end", 11.55, "72\" telescopic aluminum handle with threaded end for compatible dusters and pole tools."],
    ["mf-handle-fiberglass-60", "Fiberglass Handle — 60\"", "yellow-fiberglass-60-inch-compatible-full-handle.jpg?v=1755184015", "60-inch-fiberglass-handle-multi-color", 6.50, "60\" fiberglass handle in multiple color codes. Non-conductive, comfortable-grip everyday floor-care handle."],
    ["mf-handle-wood-55", "Solid Wood Handle — 55\"", "Wood-full-handle.jpg?v=1752404059", "55-inch-solid-wood-handle", 5.00, "55\" solid wood handle with threaded metal tip. Traditional mop-handle for looped and cut-end mop heads."],

    // ── Microfiber Pocket Mops (4 unique) ──────────────────────
    ["mf-pocket-mop-closed-loop", "Microfiber Closed-loop Mesh Pocket Mop — Color Coded", "microfiber-pocket-mop-main-group-picture-2_379a317c-7c6a-44b8-8f0e-99c88c607656.jpg?v=1746801407", "microfiber-closed-loop-mesh-pocket-mops", 5.10, "Closed-loop mesh pocket mop with color-coded binding. Slides onto pocket frames — no hook-and-loop needed."],
    ["mf-pocket-mop-dust", "Microfiber Closed-loop Mesh Pocket Dust Mop", "microfiber-pocket-mop-dust-mop-main-2.jpg?v=1746798449", "microfiber-closed-loop-mesh-pocket-dust-mop", 5.55, "Closed-loop mesh pocket dust mop — dry-mop routes with pocket-frame compatibility."],
    ["mf-pocket-mop-scrubber", "Microfiber Closed-loop Mesh Pocket Mop Scrubber", "microfiber-pocket-mop-scrubber-full-view-2.jpg?v=1746798241", "microfiber-pocket-mop-scrubber", 4.70, "Scrubber-strip pocket mop — combines soft microfiber with abrasive stripes for heavy-soil wet mopping."],
    ["mf-pocket-mop-frame", "Microfiber Pocket Mop Frames", "microfiber-pocket-mop-frame_copy_332da080-a790-4bf3-b4bb-71fab68aaeba.jpg?v=1746798050", "microfiber-pocket-mop-frame", 7.70, "Pocket-mop frame with folding pivots for one-hand pad change. Accepts all pocket-style microfiber mop heads."],

    // ── Wet Mops & Wet-Mop Hardware (19) ────────────────────────
    ["mf-wet-mop-rough-floor", "Microfiber Rough Floor Looped-End Wet Mop", "microfiber-wet-mop-group-rough-floor_f8066e16-f62e-4f13-8c40-29815025be61.jpg?v=1723748165", "microfiber-wet-mop", 8.20, "Rough-floor microfiber looped-end wet mop — quarry tile, concrete, and non-slip textured surfaces."],
    ["mf-wet-mop-hybrid-string", "Hybrid Microfiber String Wet Mop", "microfiber-wet-mop-hybrid-group_2a8561fd-c71a-45d1-8208-5c754261e0de.jpg?v=1727879007", "microfiber-wet-mop-hybrid", 6.50, "Hybrid microfiber + cotton-blend string wet mop — the absorption of cotton with the pickup power of microfiber."],
    ["mf-antimicrobial-wet-mop", "Premium Blend Antimicrobial Looped-End Wet Mop", "wet-mops-antimicrobial-looped-end-group_e401c824-9e04-47ed-8f2b-22910c5b5e2a.jpg?v=1727879311", "antimicrobial-wet-mop", 6.20, "Premium antimicrobial-treated looped-end wet mop. Hospital-safe fiber blend, EPA-registered treatment."],
    ["mf-industrial-antimicrobial-wet-mop", "Industrial Laundry-Style Antimicrobial Looped-End Wet Mop — 9\" Band", "microfiber-wet-mop-hybrid-group_e218ec6d-d68b-4c58-b664-e00f23f0bfec.jpg?v=1727879086", "industrial-wet-mop", 6.45, "Heavy-duty 9\" band industrial laundry-style antimicrobial mop. Built for commercial laundering cycles."],
    ["mf-cotton-looped-wet-mop", "Cotton Blend Looped-End Wet Mop", "wet-mop-white-narrow-large-wpe124l-group.jpg?v=1727879048", "cotton-mop", 4.45, "Cotton-blend looped-end wet mop — classic finish-water absorbent. Cost-effective daily mop."],
    ["mf-colored-looped-wet-mop", "Colored Blend Looped-End Wet Mop", "wet-mop-colored-blend-looped-ends-category-picture.jpg?v=1726074722", "looped-end-wet-mop-colored-blend", 4.45, "Colored-blend looped-end wet mop for HAI zone-coding (blue/red/yellow/green)."],
    ["mf-rayon-looped-wet-mop", "Rayon Blend Looped-End Wet Mop", "wet-mops-rayon-narrow-medium_702032d5-fa6a-4f5a-a240-587f2389d9d0.jpg?v=1726233895", "rayon-mop", 5.50, "Rayon-blend looped-end wet mop — extra absorbency for finish and wax application."],
    ["mf-premium-looped-wet-mop", "Premium Blend Economical Looped-End Wet Mop", "premium-blend-wet-mops-group-picture.jpg?v=1724344038", "looped-end-wet-mop", 4.45, "Premium-blend looped-end wet mop at economical price-point. Balanced absorption + durability."],
    ["mf-cotton-cut-end-wet-mop", "Cotton Blend Cut-End Wet Mop", "wet-mop-cut-end-cotton-24-ounce.jpg?v=1726188578", "cotton-mop-head", 3.20, "Traditional cotton cut-end wet mop — proven workhorse for daily mopping."],
    ["mf-colored-cut-end-wet-mop", "Blue Blend Cut-End Wet Mop", "wet-mop-16-ounce-blue-wide-band.jpg?v=1726189773", "colored-mop-head", 3.70, "Blue cut-end wet mop with wide band. Color-coded zone compliance."],
    ["mf-rayon-cut-end-wet-mop", "Rayon Blend Cut-End Wet Mop", "wet-mop-cut-end-rayon-16-ounce.jpg?v=1726191209", "rayon-mop-head", 4.35, "Rayon cut-end wet mop — smooth-glide floor finish and wax application."],
    ["mf-mop-bucket-side-press", "Side Press Mop Bucket & Wringer Combination Set", "wet-mop-bucket-3-square.jpg?v=1733313815", "mop-bucket", 65.00, "Side-press mop bucket + wringer combination set. Standard 26/35 qt sizes."],
    ["mf-wet-floor-sign", "Wet Floor Signs", "wet-floor-sign-square.jpg?v=1733313981", "wet-floor-sign", 8.90, "Fold-out yellow wet-floor caution sign. Bilingual English/Spanish. Slip-and-fall compliance."],
    ["mf-janitor-cart", "Janitor Carts", "Jancart1B-3_b5724146-dea4-44ab-a601-9bc6dda6da91.jpg?v=1707851632", "janitor-cart", 7.80, "Rolling janitor cart with bag, tool holders, and locking casters. Blue and gray."],
    ["mf-laundry-net", "Nylon Laundry Net with Draw String — 24\" x 36\"", "laundry-net-square.jpg?v=1733314110", "laundry-net", 7.20, "24\"x36\" nylon laundry net with draw string. Protects microfiber and mops through commercial washing."],
    ["mf-handle-gripper", "Plastic Gripper Style Wet Mop Handle", "Mop-Handle-Gripper-Blue-Fiberglass_b28b15eb-917f-4bb6-bc8a-9fa49a9ca450.jpg?v=1709063253", "wet-mop-handle-plastic-gripper", 5.50, "Plastic gripper-style wet mop handle. Squeeze-jaw quick-change on mop-head band."],
    ["mf-handle-bar-style", "Plastic Bar Style Wet Mop Handle", "Mop-Handle-Wet-Plastic-Bar-Style-Yellow_b86bbd3f-e36f-4f94-979e-4334a4070d74.jpg?v=1780153811", "wet-mop-handle-plastic-bar-style", 5.50, "Plastic bar-style wet mop handle. Traditional bar-hold with side-lever release."],
    ["mf-handle-quick-release", "Quick Release Wet Mop Handle", "Mop-Handle-Quick-Release-Wood_4ea3b4e1-bbee-4d14-8773-479d6c1e5f26.jpg?v=1780154038", "wet-mop-handle-quick-release", 5.50, "Quick-release wet mop handle. Change mop head without touching soiled fiber."],
    ["mf-handle-wood-bolt-style", "Wood Wet Mop Handle — Bolt Fitting Style", "Mop-Handle-Screw-On-Wood-full-partial.jpg?v=1726227616", "wood-wet-mop-handle-bolt-style", 8.45, "Traditional wood wet-mop handle with bolt-fitting steel tip. Fits cut-end and looped-end mop heads."],
  ].map(([slug, name, imgFile, prodPath, priceFrom, description]) => {
    const image = imgFile.startsWith("http")
      ? imgFile
      : `https://www.directmopsales.com/cdn/shop/files/${imgFile}&width=533`;
    // Pull live variants (Shopify /products/{slug}.json) with 35% markup pre-applied.
    // Prefer the case price as the primary quoted UOM; unit price is shown as secondary.
    const dms = DMS_DATA?.products?.[prodPath];
    const variants = dms?.variants || [];
    const casePrice = dms?.case_price ?? Math.round(priceFrom * 1.35 * 100) / 100;
    const unitPrice = dms?.unit_price ?? 0;
    const minPrice = dms?.min_price ?? casePrice;
    const maxPrice = dms?.max_price ?? casePrice;
    return {
    id: slug,
    name,
    category: "evs-janitorial",
    subcategory: "Microfiber",
    brand: dms?.vendor || "Direct Mop Sales",
    tagline: "Microfiber Cleaning · Sold by the Case · Sourced by Sterile Mate",
    description,
    image,
    gallery: [image],
    customer_price: casePrice,
    case_price: casePrice,
    unit_price: unitPrice,
    pack: dms?.case_label || "case",
    uom: "case",
    variants,
    features: [
      "Priced and sold by the case (bulk pack)",
      `${variants.length || "Multiple"} size / pack-count variants available`,
      "Individual-unit pricing shown for reference only",
      "Launderable / disposable options across the line",
      "Color-coded for HAI zone compliance",
      "AAMI ST79 / OSHA 1910.1030 / JCAHO EC.02.06.01 compatible workflows",
    ],
    specs: {
      "Case SKU": dms?.case_sku || slug.toUpperCase(),
      "Case Pack": dms?.case_label || "See variants",
      "Case Price": `$${casePrice.toFixed(2)}`,
      "Unit Price (ref)": unitPrice > 0 ? `$${unitPrice.toFixed(2)}` : "See variants",
      "Manufacturer": dms?.vendor || "Direct Mop Sales, Inc.",
      "Price Range": maxPrice > minPrice
        ? `$${minPrice.toFixed(2)} – $${maxPrice.toFixed(2)}`
        : `$${minPrice.toFixed(2)}`,
    },
    applications: ["Terminal room cleaning", "OR turnover", "Public-space maintenance", "Isolation room turnover", "Floor finish + wax application"],
    productUrl: dms?.url || `https://www.directmopsales.com/products/${prodPath}`,
    badge: "Case Pack"
    };
  })),
  // ============================================================
  // Texas Microfiber flat mop — featured spec-sheet SKU
  // ============================================================
  {
    id: "tm-m-01402-bl",
    name: '18" Blue Twist Face Microfiber Flat Mop — Velcro Back',
    category: "evs-janitorial",
    subcategory: "Microfiber",
    brand: "Sterile Mate",
    featured: true,
    badge: "Spec Sheet",
    imageFit: "contain",
    tagline: "Twist face yarn · foam insert · white velcro back · 240/CS",
    description:
      'Reusable 18" flat mop pad for hospital environmental services. Twist face microfiber yarn captures and holds soil and solution, a foam insert spreads chemistry evenly across the pad, and the white hook-and-loop back seats on standard 18" velcro flat-mop frames. 80/20 polyester/polyamide, launderable, blue for color-coded patient-care zones. Sold by the case — 240 pads per case.',
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/janitorial/microfiber-addons/tm_m_01402_bl_face.png`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/janitorial/microfiber-addons/tm_m_01402_bl_face.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/janitorial/microfiber-addons/tm_m_01402_bl_back.png`,
    ],
    pack: "240/CS",
    uom: "case",
    features: [
      "Twist face microfiber yarn — high soil and solution hold",
      "Foam insert distributes chemistry evenly across the pad",
      "White velcro (hook-and-loop) back — fits standard 18\" frames",
      "80/20 polyester / polyamide microfiber yarn",
      "Launderable and reusable — one charged pad per room",
      "Blue for color-coded general patient-care areas",
      "In stock · 3-day shipping · no additional tariffs or sales tax",
    ],
    specs: {
      "SKU": "TM-M-01402-BL",
      "Size": '18 in.',
      "Color": "Blue",
      "Face Construction": "Twist face microfiber yarn",
      "Composition": "80 / 20 polyester / polyamide",
      "Insert": "Foam insert",
      "Backing": "White velcro (hook & loop)",
      "Frame Compatibility": 'Standard 18" hook-and-loop frames',
      "Case Pack": "240 / CS",
      "UOM": "case",
      "Availability": "In stock · 3-day shipping",
      "Duties / Tax": "No additional tariffs or sales tax",
    },
    applications: ["Patient rooms", "Corridors", "Discharge / terminal cleaning", "Color-coded EVS programs"],
    specSheetUrl: `${process.env.REACT_APP_BACKEND_URL}/api/spec-sheets/tm-m-01402-bl.pdf`,
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/spec-sheets/tm-m-01402-bl.pdf`,
    brochureLabel: "Download specification sheet (PDF)",
  },
  // ============================================================
  // Sterile Mate — Own-Brand Disposable Microfiber Rags 12×12
  // COMING SOON — box artwork + dieline available for manufacturer preview
  // ============================================================
  {
    id: "sterilemate-disposable-microfiber-rags-12x12",
    name: 'Sterile Mate® Microfiber Cleaning Rags — 12"×12" · Wash & Reuse Up to 20×',
    category: "evs-janitorial",
    subcategory: "Microfiber",
    brand: "Sterile Mate",
    tagline: "Multi-use · Machine washable up to 20 cycles · 5 colors · Precision cut · Tear-off dispensing box · Coming Soon",
    description:
      'Sterile Mate\'s own-brand 12"×12" microfiber cleaning rag — heavy-duty 220 GSM split-fiber weave with precision ultrasonic-cut edges. Designed for demanding hospital, dental, surgical suite, and general contracting cleanup. Machine washable up to 20 launder cycles for reuse, then dispose of when performance fades. Dispensed one-at-a-time from a tear-off tuck-top box (50 rags per box). Five color-coded variants for HAI zone compliance (white/general, blue/glass & optics, yellow/high-touch, green/food-safe surfaces, black/industrial). Launch pack: 50 ct dispensing box · 8 boxes per master case (400 rags/case). Artwork and dieline files are ready for manufacturer sample production.',
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_blue.png`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_blue.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_white.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_yellow.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_green.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_black.png`,
    ],
    features: [
      'Precision ultrasonic cut edges — no fraying, no lint left behind',
      '220 GSM split microfiber — positively-charged, holds 8× its weight in fluid',
      'Machine washable up to 20 wash cycles for reuse',
      'Tear-off tuck-top dispensing box — pull one rag at a time',
      '5 color-coded variants for HAI zone compliance',
      'Standard pack: 50 ct box · 8 boxes/case (400 rags/case)',
      'Ideal for hospitals, surgical suites, dental labs, and general contracting cleanup',
      'Box style: Straight tuck-top w/ perforated dispensing oval',
      'Substrate spec provided: 18–24 pt SBS C1S · CMYK litho or flexo · matte AQ / soft-touch UV',
    ],
    specs: {
      "Cloth Size": '12" × 12"',
      "Weight": "220 GSM (heavy-duty)",
      "Fiber": "80% polyester / 20% polyamide split microfiber",
      "Edge Finish": "Ultrasonic precision cut (no sewn hem)",
      "Reusable": "Yes — machine wash up to 20 cycles",
      "Wash Instructions": "Warm water, mild detergent, no fabric softener, tumble low",
      "Colors Available": "White · Blue · Yellow · Green · Black",
      "Pack": "50 ct dispensing box",
      "Case Pack": "8 boxes / master case (400 rags)",
      "Box Dimensions": '9.25" L × 5" W × 4.75" H',
      "Country of Origin": "USA (final assembly & fulfillment)",
      "Status": "COMING SOON — Q2 2026 launch",
    },
    applications: [
      "Terminal room cleaning",
      "OR turnover",
      "Endoscope reprocessing pre-clean",
      "Dental & vet operatories",
      "General contractor jobsite cleanup",
      "Automotive detail & light industrial",
    ],
    coming_soon: true,
    launch_target: "Q2 2026",
    downloads: [
      { key: "dieline_blue",   label: "Dieline w/ Blue Artwork — For Manufacturer Samples (PDF)",   url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_dieline_blue.pdf",   primary: true },
      { key: "dieline_white",  label: "Dieline w/ White Artwork — For Manufacturer Samples (PDF)",  url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_dieline_white.pdf" },
      { key: "dieline_yellow", label: "Dieline w/ Yellow Artwork — For Manufacturer Samples (PDF)", url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_dieline_yellow.pdf" },
      { key: "dieline_green",  label: "Dieline w/ Green Artwork — For Manufacturer Samples (PDF)",  url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_dieline_green.pdf" },
      { key: "dieline_black",  label: "Dieline w/ Black Artwork — For Manufacturer Samples (PDF)",  url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_dieline_black.pdf" },
      { key: "dieline_technical", label: "Technical Dieline (cut/fold lines only, no artwork)",     url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_dieline.pdf" },
      { key: "artwork_white",  label: "Box Artwork — White (Print-Ready PDF)",  url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_box_artwork_white.pdf" },
      { key: "artwork_blue",   label: "Box Artwork — Blue (Print-Ready PDF)",   url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_box_artwork_blue.pdf" },
      { key: "artwork_yellow", label: "Box Artwork — Yellow (Print-Ready PDF)", url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_box_artwork_yellow.pdf" },
      { key: "artwork_green",  label: "Box Artwork — Green (Print-Ready PDF)",  url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_box_artwork_green.pdf" },
      { key: "artwork_black",  label: "Box Artwork — Black (Print-Ready PDF)",  url: "/api/static/microfiber-rags/sterilemate_microfiber_12x12_box_artwork_black.pdf" },
    ],
    variants: [
      { sku: "SM-MFR-12X12-WH", size: "White",  pack: "50 ct box",  available: false, is_default: true },
      { sku: "SM-MFR-12X12-BL", size: "Blue",   pack: "50 ct box",  available: false },
      { sku: "SM-MFR-12X12-YL", size: "Yellow", pack: "50 ct box",  available: false },
      { sku: "SM-MFR-12X12-GN", size: "Green",  pack: "50 ct box",  available: false },
      { sku: "SM-MFR-12X12-BK", size: "Black",  pack: "50 ct box",  available: false },
    ],
    unit_price: 0,
    case_price: 0,
    pack: "50 ct dispensing box",
    price_label: "Coming Soon — reserve your stock",
    badge: "Coming Soon",
  },
  // ============================================================
  // Sterile Processing — Sterile Mate / MicroCare (26 SKUs)
  // Instrument detergents, sterilization indicators, waste bags, and lube
  // for hospital SPDs, ORs, surgery centers, dental & vet.
  // Data lifted to @/data/sterileProcessingProducts.js so the dedicated
  // /sterile-processing landing page can share the same catalog.
  // ============================================================
  ...buildSterileProcessingProducts(),
  {
    id: "vive-dmd1045blk-digital-scale",
    name: "Digital Scale — Smart-App Compatible (Black)",
    category: "home-health-aids",
    brand: "Vive Precision",
    tagline: "400 lb capacity · Bluetooth sync to Vive Precision app",
    description: "Tempered-glass step-on scale that wirelessly connects to iOS / Android via the free Vive Precision app. Auto-off, large LED display, lbs / kg / stone units.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1045blk-digital-scale.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1045blk-digital-scale.jpg`,
    ],
    features: [
      "Wireless Bluetooth sync — Vive Precision app",
      "400 lb / 181 kg capacity",
      "Step-on activation + auto-off",
      "Tempered-glass platform with non-slip feet",
      "Bright LED display readable from any angle",
      "1-year guarantee",
    ],
    specs: {
      "SKU": "DMD1045BLK",
      "Manufacturer": "Vive Health (Vive Precision)",
      "Dimensions": "11.81\" × 12.19\" × 0.97\"",
      "Weight": "3.53 lb",
      "Power": "3× AAA batteries (included)",
      "Units": "lb · kg · stone",
      "UPC": "818323029116",
    },
    applications: ["Home health weight tracking", "Bariatric programs", "Telehealth check-ins"],
    customer_price: 20.52,
    productUrl: "https://www.vivehealth.com/products/digital-scale-compatible-with-smart-devices",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/specs/dmd1045blk-digital-scale-manual.pdf`,
    badge: "New"
  },
  {
    id: "vive-dmd1066blk-pulse-oximeter",
    name: "Pulse Oximeter Model S (Black)",
    category: "home-health-aids",
    brand: "Vive Precision",
    tagline: "SpO₂ + pulse · Bright OLED · 1-touch reading",
    description: "Compact fingertip oximeter for accurate SpO₂ and pulse-rate readings. Bright OLED display, travel case, lanyard, and AAA batteries included. Ideal for COPD, asthma, and post-COVID home monitoring.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1066blk-pulse-oximeter.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1066blk-pulse-oximeter.jpg`,
    ],
    features: [
      "Clinical-grade SpO₂ + pulse-rate accuracy",
      "Bright OLED display — easy to read",
      "One-touch operation",
      "Travel case + lanyard included",
      "2× AAA batteries included",
      "60-day Vive guarantee",
    ],
    specs: {
      "SKU": "DMD1066BLK",
      "Manufacturer": "Vive Health (Vive Precision)",
      "Dimensions": "2.36\" × 1.46\" × 1.26\"",
      "Power": "2× AAA batteries (included)",
      "Includes": "Oximeter · Case · Lanyard · Batteries",
    },
    applications: ["COPD / asthma home monitoring", "Post-surgical recovery", "Telehealth vitals"],
    customer_price: 15.83,
    productUrl: "https://www.vivehealth.com/products/pulse-oximeter",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/specs/dmd1066blk-pulse-oximeter-spec-sheet.pdf`,
    badge: "New"
  },
  {
    id: "vive-dmd1088blk-fitness-tracker",
    name: "Fitness Tracker (Mach V, Black)",
    category: "home-health-aids",
    brand: "Vive",
    tagline: "HR · SpO₂ · sleep · 16 sport modes · 7-day battery",
    description: "All-day wearable tracking sleep depth, heart rate, body temperature, daily steps, caloric intake, and hydration. 16 sport modes (4 selectable at a time), waterproof, syncs to Android/iOS via the ViveNow app.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1088blk-fitness-tracker.jpg`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/images/dmd1088blk-fitness-tracker.jpg`,
    ],
    features: [
      "Continuous HR, SpO₂, body-temp + step tracking",
      "16 sport modes (4 active at a time)",
      "Waterproof polycarbonate band",
      "Bluetooth sync — ViveNow app (Android 4.4+ / iOS 9.0+)",
      "Lightweight (24.5 g) — all-day wear",
      "60-day Vive guarantee",
    ],
    specs: {
      "SKU": "DMD1088BLK",
      "Manufacturer": "Vive Health",
      "Dimensions": "40.4 × 20.1 × 10.5 mm",
      "Weight": "24.5 g",
      "Materials": "Body PC+ABS · Band TPU",
      "Battery Life": "~7 days per charge",
    },
    applications: ["Hospital wellness programs", "Patient activity tracking", "Cardiac rehab"],
    customer_price: 26.64,
    productUrl: "https://www.vivehealth.com/products/fitness-tracker",
    brochureUrl: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/vive-health/specs/dmd1088blk-fitness-tracker-spec-sheet.pdf`,
    badge: "New"
  },
  // ============================================================
  // HEMODIALYSIS PRODUCT & CARE — sourced from sterilemate.com
  // ============================================================
  {
    id: "hemo-acid-concentrate",
    name: "Acid Concentrate (Diasol · Dryasol · Citrisol)",
    category: "hemodialysis",
    brand: "Diasol",
    tagline: "Custom formulations for every hemodialysis unit.",
    description:
      "The Diasol, Dryasol, and Citrisol lines of Acid Concentrate accommodate any hemodialysis unit — big or small, regardless of patient composition. Custom formulations eliminate on-site spiking and mixing. Diasol drivers pump-off directly into your holding tanks or totes and haul away empty drums each delivery.",
    image: `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/diasol/acid-concentrate-1.png`,
    gallery: [
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/diasol/acid-concentrate-1.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/diasol/acid-concentrate-2.png`,
      `${process.env.REACT_APP_BACKEND_URL}/api/static/marketplace/diasol/acid-concentrate-3.png`,
    ],
    features: [
      "35X, 36.83X, and 45X proportioning",
      "Dextrose 100 / 200 · Potassium 0-4 · Calcium 0-3.5",
      "Only see-through drums in the industry (color-coded)",
      "Pump-off + indoor delivery service",
      "Custom baths, spikes, and Citrisol formulations",
      "Sodium Bicarbonate in 1T, 10T, 12T, 25T totes",
    ],
    specs: {
      "Proportioning": "35X / 36.83X / 45X",
      "Formats": "Liquid concentrate · Dry powder · Bicarbonate BIG BOX",
      "Delivery": "Pump-off pump-in · empty drum haul-away",
      "Compliance": "AAMI 13959 / 11663 dialysate quality",
    },
    applications: ["In-center hemodialysis", "Acute hospital dialysis", "Home dialysis programs"],
    productUrl: "https://diasol.com/products/acid-concentrate",
    brochureUrl: "https://diasol.com/products/acid-concentrate",
    featured: true,
    badge: "Dialysis"
  },
  {
    id: "hemo-calcium-chloride",
    name: "Calcium Chloride — Dialysate Additive",
    category: "hemodialysis",
    brand: "Diasol",
    tagline: "Color-coded spike additives, 3-year shelf life.",
    description:
      "See-through packaging additives for spiking either 3.43 or 3.785 liter jugs. Color-coded labels differentiate products, 10 free additive bags per box, and dry powder additives carry a 3-year expiration date — the lowest price per spike on the market.",
    image: "https://sterilemate.com/wp-content/uploads/2022/06/additives_7a25517c-93f6-4f51-abf8-e68af7533145_360x.webp",
    features: [
      "Color-coded labels differentiate every SKU",
      "See-through packaging for easy mixing",
      "Spike sizes for 3.43 L or 3.785 L jugs",
      "10 free additive bags in each box",
      "3-year expiration date on dry powder",
    ],
    specs: {
      "Available Amounts": "5.1 g / 5.7 g / 6.3 g / 9.7 g / 10.2 g / 11.3 g / 12.5 g",
      "Series": "35X · 36.83X · 45X",
      "Units per Box": "120 – 220 depending on series",
    },
    applications: ["Acid concentrate spiking", "Custom dialysate baths"],
    productUrl: "https://sterilemate.com/product/calcium-chloride/",
    badge: "Additive"
  },
  {
    id: "hemo-potassium-chloride",
    name: "Potassium Chloride — Dialysate Additive",
    category: "hemodialysis",
    brand: "Diasol",
    tagline: "K+ spike additives for custom baths.",
    description:
      "Potassium chloride dry-powder additives in color-coded, see-through packaging. Spike your Diasol acid concentrate jugs to the exact potassium level ordered by the prescribing nephrologist — 0 to 4 mEq/L.",
    image: "https://sterilemate.com/wp-content/uploads/2022/06/additives_066a24dc-2291-4479-9e39-e71a4c2519ae_360x.webp",
    features: [
      "Precise K+ dose per jug",
      "Color-coded per potassium level",
      "10 free additive bags per box",
      "3-year expiration on dry powder",
      "Compatible with all Diasol acid concentrates",
    ],
    specs: {
      "Available Amounts": "5.8 g / 6.3 g / 9.8 g / 10.4 g / 11.5 g / 12.7 g / 15.9 g",
      "Series": "35X · 36.83X · 45X",
      "Units per Box": "120 – 170 depending on series",
    },
    applications: ["Acid concentrate spiking", "Custom potassium baths"],
    productUrl: "https://sterilemate.com/product/potassium-chloride/",
    badge: "Additive"
  },
  {
    id: "hemo-ascorbic-acid",
    name: "Ascorbic Acid — Dialysate Additive",
    category: "hemodialysis",
    brand: "Diasol",
    tagline: "Vitamin-C additive for oxidative-stress protocols.",
    description:
      "Ascorbic acid additive for hemodialysis facilities running vitamin-C supplementation protocols. Color-coded, see-through packaging with 10 free bags per box and a 3-year shelf life on the dry powder.",
    image: "https://sterilemate.com/wp-content/uploads/2022/06/additives_360x.webp",
    features: [
      "2.0 g precision dose per bag",
      "Color-coded label",
      "See-through packaging for verification",
      "10 free additive bags per box",
      "3-year expiration on dry powder",
    ],
    specs: {
      "Amount": "2.0 grams",
      "Series": "45X",
      "Units per Box": "260",
    },
    applications: ["Vitamin-C dialysate spiking", "Oxidative-stress protocols"],
    productUrl: "https://sterilemate.com/product/ascorbic-acid/",
    badge: "Additive"
  },
  {
    id: "hemo-mixers",
    name: "Concentrate Mixer — 40 / 80 Gallon Auto-Batch",
    category: "hemodialysis",
    brand: "Diasol",
    tagline: "Automated acid concentrate mixing station.",
    description:
      "Produces 40 or 80 gallons of acid concentrate per batch. Compatible with Diasol Acid Concentrate and CitriSol Concentrate Boxes (20 Gal. mix per box). Automatic water level adjustment, timed mixing, and a digital checklist that monitors every batch. Fits through standard 30\" doors.",
    image: "https://sterilemate.com/wp-content/uploads/2022/06/mixer1_480x480.webp",
    gallery: [
      "https://sterilemate.com/wp-content/uploads/2022/06/mixer1_480x480.webp",
      "https://sterilemate.com/wp-content/uploads/2022/06/mixer3_480x480.webp",
    ],
    features: [
      "40 or 80 gallon batch capacity",
      "Automatic water-level adjustment",
      "Timed mixing with digital checklist",
      "Cleaning cycle locked to prevent tampering",
      "Manual / emergency override",
      "Fits through 30\"+ standard doorways",
      "Ergonomic — comfortable for operators of any height",
    ],
    specs: {
      "Batch Size": "40 gal / 80 gal",
      "Compatible Concentrates": "Diasol · CitriSol (20 Gal. mix/box)",
      "Footprint": "Fits 30\"+ standard doorway",
    },
    applications: ["In-center dialysis clinics", "Hospital dialysis units"],
    productUrl: "https://sterilemate.com/product/mixers/",
    brochureUrl: "https://sterilemate.com/wp-content/uploads/2022/06/ConcentrateMixer.pdf",
    featured: true,
    badge: "Equipment"
  },
  {
    id: "hemo-solace-scale",
    name: "Solace In-Floor Dialysis Scale — 1,000 lb",
    category: "hemodialysis",
    brand: "Solace",
    tagline: "Recessed 1,000 lb capacity scale with touchscreen indicator.",
    description:
      "1,000 lb x 0.2 lb (450 kg x 0.1 kg) capacity in-floor dialysis scale with multi-color touchscreen indicator/printer combo. Four standard platform sizes — up to a 6' × 4' deck that accommodates stretchers. Optional handrails available.",
    image: "https://sterilemate.com/wp-content/uploads/2023/05/ID-7248SH-855RMP_Cutaway-View.jpg",
    gallery: [
      "https://sterilemate.com/wp-content/uploads/2023/05/ID-7248SH-855RMP_Cutaway-View.jpg",
      "https://sterilemate.com/wp-content/uploads/2023/05/ID-4836S-855RMP_Dialysis-Chair_Low-Angle.jpg",
      "https://sterilemate.com/wp-content/uploads/2023/05/ID-4848SH-855RMP.jpg",
      "https://sterilemate.com/wp-content/uploads/2023/05/ID-7248S-855RMP_Stretcher_Facing-Right.jpg",
    ],
    features: [
      "1,000 lb x 0.2 lb / 450 kg x 0.1 kg capacity",
      "4 standard platform sizes (up to 6' × 4')",
      "Multi-color touchscreen indicator + printer",
      "Recessed installation — flush with finished floor",
      "Handrails available",
      "Stretcher-compatible on 6' × 4' deck",
    ],
    specs: {
      "Capacity": "1,000 lb x 0.2 lb (450 kg x 0.1 kg)",
      "Platform Sizes": "36×36 · 48×36 · 48×48 · 72×48 (inches)",
      "Indicator": "855 Recessed Wall-Mount w/ Printer",
      "Deck": "Stainless steel",
    },
    applications: ["In-center dialysis weighing", "Pre/post treatment fluid assessment"],
    productUrl: "https://sterilemate.com/product/solace-in-floor-dialysis-scales/",
    badge: "Equipment"
  },
  {
    id: "hemo-uv-infusion-set",
    name: "Disposable UV-Protected Infusion Set",
    category: "hemodialysis",
    brand: "Gemtier",
    tagline: "Protects photosensitive drugs during infusion.",
    description:
      "Nursing-protection infusion set that prevents photosensitive drugs from denaturation and degradation during administration. Auto de-bubble and auto air-venting with a precision AccuFilter™ and inner-layer DEHP-free TPU tubing.",
    image: "https://sterilemate.com/wp-content/uploads/2022/12/b5808a45fa.jpg",
    features: [
      "UV protection preserves drug potency",
      "Auto de-bubble in drip chamber",
      "Auto air-venting",
      "DEHP-free TPU inner layer",
      "AccuFilter™ precision particle filter",
      "Double-pipe composite structure",
    ],
    specs: {
      "Filter": "AccuFilter™ 2/3/5 µm options",
      "Tubing": "DEHP-free TPU",
      "Compliance": "For photosensitive parenteral drugs",
    },
    applications: ["Photosensitive drug infusion", "Chemotherapy administration", "ICU"],
    productUrl: "https://sterilemate.com/product/disposable-uv-protected-infusion-sets/",
    badge: "Consumable"
  },
  {
    id: "hemo-safety-infusion-fbv",
    name: "Safety Infusion Set (FBV) — Flow Blocking Valve",
    category: "hemodialysis",
    brand: "Gemtier",
    tagline: "Prevents air-embolism and blood backflow.",
    description:
      "Flow Blocking Valve (FBV) in the drip chamber prevents bubbles from entering the tube below the chamber and auto-blocks flow the moment the drip chamber runs empty. Four SKUs — FBV, FBV-TPE, FBV-F, FBV-F-TPE.",
    image: "https://sterilemate.com/wp-content/uploads/2022/12/18779e9a5f.jpg",
    features: [
      "Prevents bubbles from entering the patient",
      "Auto-stops flow when drip chamber empties",
      "Safe container switching",
      "Prevents blood backflow",
      "TPE variants available for DEHP-free protocols",
    ],
    specs: {
      "Reorder Numbers": "INS-1201 · INS-1201T · INS-1202 · INS-1202T",
      "Series": "INS 1000",
      "Materials": "Standard + TPE (DEHP-free) options",
    },
    applications: ["Infusion therapy", "Chemotherapy", "Emergency IV administration"],
    productUrl: "https://sterilemate.com/product/safety-infusion-setfbv/",
    badge: "Consumable"
  },
  // ============================================================
  // FACILITY BUILDING SUPPLIES — Clear Vinyl
  // ============================================================
  {
    id: "vr-16m-clr",
    name: "Clear 16 Mil PVC Vinyl — Polyester Scrim 1000D (6x6)",
    sku: "VR-16M-CLR",
    category: "facility-supplies",
    subcategory: "clear-vinyl",
    brand: "TARPS NOW",
    tagline: "Heavy-duty industrial roll goods — 32\" x 100 yard rolls.",
    description:
      "Optically clear 16-mil PVC vinyl laminated over a 1000-denier polyester scrim in a 6x6 cross-weave grid. Tear-resistant and dimensionally stable — ideal for hospital strip curtains, ICRA-compliant dust barriers, cleanroom partitions, food-processing doorways, and warehouse dock curtains. Ships on 2\" or 3\" cores.",
    image: "/api/static/facility-supplies/vr-16m-clr-clear-vinyl.png",
    gallery: ["/api/static/facility-supplies/vr-16m-clr-clear-vinyl.png"],
    features: [
      "16-mil clear PVC vinyl",
      "1000-denier polyester scrim (6x6 cross-weave)",
      "32\" roll width  ·  100 yards per roll",
      "Choice of 2\" or 3\" core",
      "Shipping included on 350-roll bulk quote",
      "Serving TX / LA / AL / MS",
    ],
    specs: {
      "SKU": "VR-16M-CLR",
      "Manufacturer": "TARPS NOW",
      "Material": "PVC Vinyl w/ Polyester Scrim",
      "Reinforcement": "1000 Denier (6x6 scrim)",
      "Thickness": "16 mil",
      "Color": "Clear",
      "Roll Width": "32 in",
      "Core Size": "2 in or 3 in",
      "Roll Length": "100 yards",
      "Unit Price": "$460.00 / roll (shipping included)",
      "Bulk Quote (350 rolls)": "$161,000.00 (plus tax) — 35,000 yards total",
      "Lead Time": "16 – 18 weeks",
    },
    applications: [
      "Hospital strip curtains (traffic / temperature control)",
      "ICRA Class III/IV dust & infection-control barriers",
      "Cleanroom & pharmacy compounding partitions",
      "Food processing / cold storage doorway curtains",
      "Warehouse dock & loading-bay barriers",
      "Machine guarding & spray-containment enclosures",
    ],
    productUrl: null,
    brochureUrl: "/api/static/facility-supplies/vr-16m-clr-spec-sheet.pdf",
    featured: true,
    badge: "Downloadable Quote",
  },
];

// Product Card Component — Amazon-style layout with SKU + UOM + qty input
const ProductCard = ({ product, onViewDetails }) => {
  const { addItem, setShowCart } = useQuoteCart();
  const [qty, setQty] = useState(1);
  const isFilmop = product.brand === "Filmop USA";
  const sku = product.specs?.["Part Number"] || product.specs?.SKU || product.id;
  const uom =
    (typeof product.pack === "string" && product.pack) ||
    product.pack?.display ||
    product.specs?.UOM ||
    product.specs?.["Unit of Measure"] ||
    (Number(product.case_price) > 0 ? "case" : "each");
  const price = Number(product.case_price ?? product.customer_price ?? 0);
  const handleAdd = () => {
    addItem({
      erp: sku,
      name: product.name,
      image: product.image,
      source: product.brand || "marketplace",
      category: product.category,
      unit_price: price,
      list_price: price,
      pack: uom,
      uom,
    }, qty);
    setShowCart(true);
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all group flex flex-col" data-testid={`product-card-${product.id}`}>
      <div className="relative cursor-pointer" onClick={() => onViewDetails(product)}>
        <img 
          src={product.image} 
          alt={product.name}
          className={`w-full group-hover:scale-105 transition-transform duration-300 ${isFilmop || product.imageFit === "contain" ? 'h-40 object-contain bg-slate-50 p-3' : 'h-48 object-cover'}`}
        />
        {product.badge && (
          <Badge className={`absolute top-3 right-3 ${
            product.badge === 'Best Seller' ? 'bg-amber-500' :
            product.badge === 'New' ? 'bg-emerald-500' :
            product.badge === 'Popular' ? 'bg-blue-500' :
            product.badge === 'Smart' ? 'bg-violet-500' :
            product.badge === 'AAMI ST108' ? 'bg-cyan-600' :
            product.badge === 'Eco-Friendly' ? 'bg-green-600' :
            'bg-slate-700'
          } text-white`}>
            {product.badge}
          </Badge>
        )}
        {product.featured && (
          <div className="absolute top-3 left-3">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
          </div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{product.brand}</p>
        <h3
          className="font-bold text-slate-900 mb-1 line-clamp-2 cursor-pointer hover:text-emerald-700 transition-colors leading-snug"
          onClick={() => onViewDetails(product)}
        >
          {product.name}
        </h3>
        {product.tagline && (
          <p className="text-xs text-emerald-700 font-semibold mb-2 line-clamp-1">{product.tagline}</p>
        )}
        {/* SKU + UOM row — Amazon-style spec strip */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] mb-2 pb-2 border-b border-slate-100">
          <span className="text-slate-500 uppercase tracking-wide font-bold text-[9px]">SKU</span>
          <span className="font-mono font-semibold text-slate-800" data-testid={`product-sku-${product.id}`}>{sku}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500 uppercase tracking-wide font-bold text-[9px]">UOM</span>
          <span className="font-semibold text-slate-800" data-testid={`product-uom-${product.id}`}>{uom}</span>
        </div>
        <p className="text-xs text-slate-600 line-clamp-2 mb-3 flex-1">{product.description}</p>
        {/* Price + In-stock line */}
        <div className="flex items-baseline justify-between mb-3">
          {price > 0 ? (
            <div className="flex items-baseline gap-1" data-testid={`product-price-${product.id}`}>
              <span className="text-xl font-black text-slate-900">${price.toFixed(2)}</span>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">/ {uom}</span>
            </div>
          ) : (
            <span className="text-xs text-slate-500 italic">Request pricing</span>
          )}
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">In Stock</span>
        </div>
        {Number(product.unit_price) > 0 && Number(product.unit_price) !== price && (
          <p className="text-[11px] text-slate-500 -mt-2 mb-2" data-testid={`product-unit-ref-${product.id}`}>
            <span className="opacity-75">Ref:</span> ${Number(product.unit_price).toFixed(2)} per unit
          </p>
        )}
        {/* Qty + Add + Details — Amazon-style dual CTA */}
        <div className="grid grid-cols-[auto_1fr] gap-2">
          <input
            type="number"
            min={1}
            step={1}
            value={qty}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              setQty(Number.isFinite(n) && n >= 1 ? n : 1);
            }}
            onFocus={(e) => e.target.select()}
            onWheel={(e) => e.currentTarget.blur()}
            className="w-14 h-9 text-center text-sm font-bold border border-slate-300 rounded focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
            aria-label={`Quantity for ${product.name}`}
            data-testid={`product-qty-${product.id}`}
          />
          <Button
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold shadow-sm"
            onClick={handleAdd}
            data-testid={`product-add-to-cart-${product.id}`}
          >
            <ShoppingCart className="w-4 h-4 mr-1" />
            Add to Cart
          </Button>
          <button
            className="col-span-2 text-xs font-semibold text-slate-600 hover:text-emerald-700 hover:underline text-center py-1 transition-colors"
            onClick={() => onViewDetails(product)}
            data-testid={`product-view-details-${product.id}`}
          >
            View full details →
          </button>
          {product.specSheetUrl && (
            <a
              href={product.specSheetUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded border border-amber-300 bg-amber-50 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100 transition-colors"
              data-testid={`product-spec-sheet-${product.id}`}
            >
              <Download className="w-3.5 h-3.5" /> Spec sheet (PDF)
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Green Klean Product Card Component
const GreenKleanProductCard = ({ product, onViewDetails }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all group">
      <div className="relative">
        <img 
          src={product.image_url || "https://gogreenklean.com/wp-content/uploads/2023/02/Green-Klean-Logo.png"} 
          alt={product.description}
          className="w-full h-48 object-contain bg-slate-50 p-4 group-hover:scale-105 transition-transform duration-300"
        />
        {product.featured && (
          <Badge className="absolute top-3 right-3 bg-emerald-600 text-white">
            Featured
          </Badge>
        )}
        <div className="absolute top-3 left-3">
          <Badge variant="outline" className="bg-white/90 text-emerald-700 border-emerald-300">
            {product.item_number}
          </Badge>
        </div>
      </div>
      <div className="p-4">
        <p className="text-xs text-emerald-600 font-semibold mb-1">Green Klean®</p>
        <h3 className="font-bold text-slate-800 mb-1 line-clamp-2 text-sm">{product.description}</h3>
        {product.fits && (
          <p className="text-xs text-slate-500 mb-2 line-clamp-1">Fits: {product.fits}</p>
        )}
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-lg font-bold text-emerald-700">${product.customer_price?.toFixed(2)}</p>
            <p className="text-xs text-slate-500">{product.pack_info || product.uom}</p>
          </div>
          {product.in_stock && (
            <Badge variant="outline" className="text-emerald-600 border-emerald-300">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              In Stock
            </Badge>
          )}
        </div>
        <Button 
          size="sm" 
          className="w-full bg-emerald-600 hover:bg-emerald-700"
          onClick={() => onViewDetails(product)}
        >
          <Eye className="w-4 h-4 mr-1" />
          View Details
        </Button>
      </div>
    </div>
  );
};

// Green Klean Product Detail Modal
const GreenKleanDetailModal = ({ product, onClose }) => {
  const { addItem } = useQuoteCart();
  if (!product) return null;
  
  const hasPrice = Number(product.customer_price) > 0;
  const handleAddToCart = () => {
    addItem({
      erp: product.item_number,
      name: product.description || product.item_number,
      image: product.image_url,
      source: "marketplace",
      category: "green-klean",
      unit_price: Number(product.customer_price) || 0,
    });
    onClose();
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="relative bg-gradient-to-r from-emerald-700 to-emerald-600 p-6">
          <div className="flex items-start gap-6">
            <img 
              src={product.image_url || "https://gogreenklean.com/wp-content/uploads/2023/02/Green-Klean-Logo.png"} 
              alt={product.description}
              className="w-32 h-32 object-contain bg-white rounded-lg p-2"
            />
            <div className="flex-1 text-white">
              <Badge className="bg-white/20 text-white mb-2">{product.item_number}</Badge>
              <h2 className="text-xl font-bold mb-2">{product.description}</h2>
              <p className="text-emerald-100 text-sm">Manufacturer: Green Klean®</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Price & Availability */}
          <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
            <div>
              <p className="text-sm text-slate-600">Price per {product.uom || "Case"}</p>
              <p className="text-3xl font-bold text-emerald-700">${product.customer_price?.toFixed(2)}</p>
              <p className="text-sm text-slate-500">{product.pack_info}</p>
            </div>
            <div className="text-right">
              {product.in_stock ? (
                <Badge className="bg-emerald-600 text-white">
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  In Stock
                </Badge>
              ) : (
                <Badge variant="outline">Out of Stock</Badge>
              )}
              <p className="text-sm text-slate-500 mt-2">Lead Time: {product.lead_time_days || 3} days</p>
            </div>
          </div>
          
          {/* Compatibility */}
          {product.fits && (
            <div>
              <h3 className="font-bold text-slate-800 mb-2">Compatible With</h3>
              <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{product.fits}</p>
            </div>
          )}
          
          {/* OEM Part Numbers */}
          {product.oem_part_numbers && (
            <div>
              <h3 className="font-bold text-slate-800 mb-2">Replaces OEM Part Numbers</h3>
              <div className="flex flex-wrap gap-2">
                {product.oem_part_numbers.split(',').map((part, i) => (
                  <Badge key={i} variant="outline" className="font-mono">{part.trim()}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Brand Compatibility */}
          {product.brand_compatibility && product.brand_compatibility.length > 0 && (
            <div>
              <h3 className="font-bold text-slate-800 mb-2">Works With Brands</h3>
              <div className="flex flex-wrap gap-2">
                {product.brand_compatibility.map((brand, i) => (
                  <Badge key={i} className="bg-slate-100 text-slate-700">{brand}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Features */}
          <div>
            <h3 className="font-bold text-slate-800 mb-3">Green Klean® Quality Promise</h3>
            <div className="grid md:grid-cols-2 gap-2">
              {[
                "Micro-Plus filter media for superior filtration",
                "Designed specifically for commercial use",
                "Equal to or exceed OEM quality",
                "Green Diamond Warranty backed",
                "Reduces dust and improves air quality",
                "Easy vacuum identification printing"
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  {feature}
                </div>
              ))}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-200">
            {hasPrice ? (
              <Button
                onClick={handleAddToCart}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="gk-modal-add-to-cart-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Add to Quote Cart
              </Button>
            ) : (
              <Button className="bg-emerald-600 hover:bg-emerald-700" asChild>
                <Link
                  to={`/hospital/backorders?prefill=1&source=marketplace&item_name=${encodeURIComponent(product.description || product.item_number || "")}&mfr_number=${encodeURIComponent(product.item_number || "")}&brand=${encodeURIComponent("Green Klean")}`}
                  data-testid="gk-modal-request-quote-btn"
                  onClick={onClose}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Request Quote
                </Link>
              </Button>
            )}
            <Button variant="outline" asChild>
              <a href="tel:2814563458">
                <Phone className="w-4 h-4 mr-2" />
                Call 281.456.3458
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href="https://sterilemate-ims-hart.preview.emergentagent.com/api/pdf-brochures/green-klean-price-guide/download" target="_blank" rel="noopener noreferrer">
                <Download className="w-4 h-4 mr-2" />
                Price Guide PDF
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default function MarketplacePage() {
  // Read URL query params so links like /marketplace?category=home-health-aids&sub=Blood%20Pressure%20Monitors work
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialCategory = searchParams.get("category") || "evs-janitorial";
  const initialSub = searchParams.get("sub") || "all";

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedGreenKleanProduct, setSelectedGreenKleanProduct] = useState(null);
  const navigate = useNavigate();
  // Route the "View details" action from every product card to the full-screen
  // product page. The modal path is kept as a fallback for edge cases (Green Klean
  // catalog still uses its own dedicated modal component below).
  const goToProductPage = (product) => {
    if (!product?.id) return;
    navigate(`/marketplace/product/${product.id}`);
  };
  const [greenKleanProducts, setGreenKleanProducts] = useState([]);
  const [greenKleanLoading, setGreenKleanLoading] = useState(false);
  const [greenKleanSubcategory, setGreenKleanSubcategory] = useState("all");
  const [microcareProducts, setMicrocareProducts] = useState([]);  // (legacy — no longer used)
  const [microcarePricing, setMicrocarePricing] = useState({});
  // NEW: sub-chip filter for Home Health Care Aids
  const [hhcaSubcategory, setHhcaSubcategory] = useState(
    initialCategory === "home-health-aids" ? initialSub : "all"
  );
  // NEW: sub-chip filter for Janitorial & EVS (Filmop / Microfiber / Green Klean-in-catalog / etc.)
  const [evsSubcategory, setEvsSubcategory] = useState(
    initialCategory === "evs-janitorial" ? initialSub : "all"
  );
  // Which color box variant is displayed inside the Microfiber launch hero
  const [microfiberColor, setMicrofiberColor] = useState("blue");

  // Live Backorder Inventory row count — pulled from the same public endpoint
  // that powers /backorders/live-inventory so the marketing number stays
  // in-sync with the actual list.
  const [liveInvCount, setLiveInvCount] = useState(null);
  const [liveInvUpdated, setLiveInvUpdated] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/live-inventory`);
        const d = await r.json();
        if (!alive) return;
        setLiveInvCount(typeof d?.row_count === "number" ? d.row_count : null);
        setLiveInvUpdated(d?.updated_at || null);
      } catch { /* silent — hero still renders with a fallback */ }
    })();
    return () => { alive = false; };
  }, []);

  // Sync selectedCategory + subcategory state when the URL query string changes
  // (e.g. user clicks a different category in the persistent sidebar without
  // remounting the page).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get("category") || "evs-janitorial";
    const sub = params.get("sub") || "all";
    setSelectedCategory(cat);
    setHhcaSubcategory(cat === "home-health-aids" ? sub : "all");
    setEvsSubcategory(cat === "evs-janitorial" ? sub : "all");
    // scroll top so users see the new category
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.search]);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Fetch Green Klean products from API
  useEffect(() => {
    const fetchGreenKleanProducts = async () => {
      setGreenKleanLoading(true);
      try {
        // Fetch Green Klean products specifically by searching for GK- item numbers
        const response = await fetch(`${API_URL}/api/marketplace/products?search=GK-&limit=100`);
        const data = await response.json();
        // Filter to only Green Klean products (manufacturer check)
        const gkProducts = data.products.filter(p => p.manufacturer === "Green Klean");
        setGreenKleanProducts(gkProducts);
      } catch (error) {
        console.error("Error fetching Green Klean products:", error);
      } finally {
        setGreenKleanLoading(false);
      }
    };
    
    fetchGreenKleanProducts();
  }, [API_URL]);

  // Fetch MicroCare v2 catalog and MERGE pricing/variants INTO the existing
  // hardcoded MicroCare products (matched by product-family keywords → slug).
  // Uses the shared helper so /sterile-processing gets identical pricing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bySlug = await fetchMicrocarePricing(API_URL);
        if (!cancelled) setMicrocarePricing(bySlug);
      } catch (e) { console.error("MicroCare merge failed", e); }
    })();
    return () => { cancelled = true; };
  }, [API_URL]);
  
  // Get unique subcategories for Green Klean
  const greenKleanSubcategories = [...new Set(greenKleanProducts.map(p => p.subcategory).filter(Boolean))];
  
  // Filter Green Klean products by subcategory
  const filteredGreenKleanProducts = greenKleanSubcategory === "all" 
    ? greenKleanProducts 
    : greenKleanProducts.filter(p => p.subcategory === greenKleanSubcategory);
  
  // Filter products (base PRODUCTS enriched with MicroCare pricing merged by slug)
  const ALL_PRODUCTS = PRODUCTS.map((p) => {
    const enrich = microcarePricing[p.id];
    if (!enrich || !enrich.default_variant) return p;
    const dv = enrich.default_variant;
    return {
      ...p,
      customer_price: dv.case_price ?? p.customer_price,
      unit_price: dv.unit_price ?? p.unit_price,
      case_price: dv.case_price ?? p.case_price,
      pack: dv.pack ?? p.pack,
      variants: enrich.variants,
      is_solm: false,
    };
  });
  const filteredProducts = ALL_PRODUCTS.filter(product => {
    const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
    // NEW: sub-chip filter for Home Health Care Aids
    const matchesHhcaSub = selectedCategory !== "home-health-aids"
      || hhcaSubcategory === "all"
      || product.subcategory === hhcaSubcategory;
    // NEW: sub-chip filter for Janitorial & EVS
    const matchesEvsSub = selectedCategory !== "evs-janitorial"
      || evsSubcategory === "all"
      || product.subcategory === evsSubcategory;
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesHhcaSub && matchesEvsSub && matchesSearch;
  }).sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  // Unique subcategories for Home Health Care Aids
  const hhcaSubcategories = [
    ...new Set(
      PRODUCTS.filter((p) => p.category === "home-health-aids" && p.subcategory)
              .map((p) => p.subcategory)
    ),
  ];
  // Unique subcategories for Janitorial & EVS
  const evsSubcategories = [
    ...new Set(
      PRODUCTS.filter((p) => p.category === "evs-janitorial" && p.subcategory)
              .map((p) => p.subcategory)
    ),
  ];
  
  // Get featured products — when a sub-chip filter is active, only the featured
  // items inside that filter are surfaced (e.g. the Microfiber tab).
  const subFilterActive = evsSubcategory !== "all" || hhcaSubcategory !== "all";
  const featuredProducts = subFilterActive
    ? filteredProducts.filter(p => p.featured)
    : PRODUCTS.filter(p => p.featured);
  
  // Build breadcrumb trail from current filter state
  const currentCategoryDef = CATEGORIES.find((c) => c.id === selectedCategory);
  const breadcrumbTrail = [];
  if (currentCategoryDef) {
    breadcrumbTrail.push({ label: currentCategoryDef.name });
    // If a sub-chip is active, append it
    if (selectedCategory === "home-health-aids" && hhcaSubcategory !== "all") {
      breadcrumbTrail[0].to = "/marketplace?category=home-health-aids";
      breadcrumbTrail.push({ label: hhcaSubcategory });
    } else if (selectedCategory === "evs-janitorial" && evsSubcategory !== "all") {
      breadcrumbTrail[0].to = "/marketplace?category=evs-janitorial";
      breadcrumbTrail.push({ label: evsSubcategory });
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      <MarketplaceCategoryNav currentCategory={
        selectedCategory === "sterile-processing" ? "sterile-processing"
        : selectedCategory === "hemodialysis" ? "hemodialysis"
        : "marketplace-home"
      } />

      <MarketplaceBreadcrumb trail={breadcrumbTrail} />

      {/* Utility bar (light) */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Back to Dashboard</span>
          </Link>
          <a href="tel:8009883892" className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900">
            <Phone className="w-3.5 h-3.5" />
            800.988.3892
          </a>
        </div>
      </div>

      {/* ── Compact hospital-buyer band (1 line, no clutter) ──
          Replaces the previous stacked headline + 4-step flow + value cards
          + sourcing hero. Buyers see the key trust signals + CTAs in a
          single strip and get to product tiles immediately.  */}
      <div className="bg-gradient-to-r from-cyan-50 via-white to-emerald-50 border-b border-slate-200" data-testid="marketplace-buyer-band">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-slate-700">
            <span className="inline-flex items-center gap-1.5 font-semibold">
              <ShoppingBag className="w-3.5 h-3.5 text-cyan-700" /> Free hospital account · 60 seconds
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5 font-semibold">
              <FileText className="w-3.5 h-3.5 text-emerald-700" /> Net-30 terms available
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5 font-semibold">
              <BadgeCheck className="w-3.5 h-3.5 text-amber-700" /> Real people · No card checkouts
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/hospital/login?next=${encodeURIComponent(location.pathname + location.search)}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white text-[11px] font-bold transition-all"
              data-testid="marketplace-create-account-cta"
            >
              Create account
            </Link>
            <Link
              to={`/hospital/login?next=${encodeURIComponent(location.pathname + location.search)}&intent=net30`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 hover:border-slate-900 text-slate-800 text-[11px] font-bold transition-all"
              data-testid="marketplace-net30-cta"
            >
              Request Net-30
            </Link>
            <a
              href="tel:8009883892"
              className="inline-flex items-center gap-1.5 text-slate-700 hover:text-slate-900 text-[11px] font-bold"
              data-testid="marketplace-call-cta"
            >
              <Phone className="w-3 h-3" /> 800.988.3892
            </a>
          </div>
        </div>
      </div>

      {/* Slim access banner — routes buyers to the Live Backorder List
          without competing with the product grid. */}
      <Link
        to="/backorders/live-inventory"
        data-testid="marketplace-backorder-banner"
        className="group block border-b border-slate-200 bg-slate-50 hover:bg-white transition-colors"
      >
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[13px]">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-amber-800 bg-amber-100 ring-1 ring-amber-300 px-2 py-0.5 rounded">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Live
            </span>
            <span className="font-semibold text-slate-800 truncate">
              Weekly Deals &amp; Hard-to-Find Backorder List
            </span>
            {liveInvCount != null && (
              <span className="hidden sm:inline text-slate-500">
                &middot; <span className="font-semibold text-slate-700">{liveInvCount} SKUs</span> aggregated from our national supplier partners
              </span>
            )}
          </div>
          <span className="inline-flex items-center gap-1.5 text-cyan-800 font-bold group-hover:gap-2 transition-all whitespace-nowrap">
            Browse the Live List
            <ChevronRight className="w-4 h-4" />
          </span>
        </div>
      </Link>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Sterile Mate Microfiber Rags launch cover — shown when user selects EVS → Microfiber sub-filter */}
        {selectedCategory === "evs-janitorial" && evsSubcategory === "Microfiber" && (
          <section
            data-testid="sterilemate-microfiber-launch-hero"
            className="mb-10 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white shadow-2xl"
          >
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left: hero product photo — swappable via color swatches */}
              <div className="relative bg-gradient-to-br from-cyan-500/10 to-blue-800/40 flex items-center justify-center p-4 md:p-6">
                <img
                  key={microfiberColor}
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_${microfiberColor}.png`}
                  alt={`Sterile Mate ${microfiberColor} Microfiber Cleaning Rags — 12x12 dispensing box`}
                  className="w-full h-auto rounded-xl shadow-xl transition-opacity duration-300"
                  data-testid="sterilemate-microfiber-hero-img"
                />
                {/* Color swatches — click to switch the image */}
                <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {["blue", "white", "yellow", "green", "black"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setMicrofiberColor(c)}
                        aria-label={`Show ${c} variant`}
                        aria-pressed={microfiberColor === c}
                        className={`w-9 h-9 rounded-full border-2 shadow-lg transition-transform hover:scale-110 ${microfiberColor === c ? "border-cyan-300 ring-2 ring-cyan-300/60 scale-110" : "border-white/70"}`}
                        style={{
                          background:
                            c === "white" ? "#f5f5f5"
                            : c === "yellow" ? "#facc15"
                            : c === "green" ? "#22c55e"
                            : c === "black" ? "#111827"
                            : "#2563eb",
                        }}
                        title={c[0].toUpperCase() + c.slice(1)}
                        data-testid={`microfiber-swatch-${c}`}
                      />
                    ))}
                  </div>
                  {/* Download the currently-displayed image */}
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_${microfiberColor}.png`}
                    download={`sterilemate-microfiber-${microfiberColor}.png`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/95 hover:bg-white text-slate-900 font-bold text-xs px-3 py-2 shadow-lg transition-colors"
                    data-testid="microfiber-download-current-image"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download {microfiberColor[0].toUpperCase() + microfiberColor.slice(1)} Image
                  </a>
                </div>
              </div>

              {/* Right: copy + CTAs */}
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 self-start rounded-full bg-amber-500 text-slate-900 font-bold text-[11px] uppercase tracking-widest px-3 py-1.5 mb-4">
                  <Star className="w-3.5 h-3.5 fill-slate-900" />
                  New Sterile Mate Own-Brand · Coming Q2 2026
                </div>
                <h1 className="text-3xl md:text-4xl font-black leading-tight mb-3">
                  Sterile Mate<sup>®</sup> Microfiber Cleaning Rags
                  <span className="block text-cyan-300 text-2xl md:text-3xl mt-1">
                    12&quot;×12&quot; · Wash &amp; Reuse Up to 20×
                  </span>
                </h1>
                <p className="text-base text-white/85 mb-5 leading-relaxed">
                  Sterile Mate&apos;s own-brand microfiber cleaning rag — 220 GSM
                  precision-cut, machine-washable up to 20 cycles, dispensed
                  one-at-a-time from a tear-off tuck-top box. Currently viewing:
                  <span className="font-bold text-cyan-300 ml-1">
                    {microfiberColor[0].toUpperCase() + microfiberColor.slice(1)}
                  </span>{" "}
                  variant — click a color dot to switch.
                </p>

                <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-white/90 mb-6">
                  {[
                    "Precision ultrasonic cut edges",
                    "220 GSM heavy-duty split fiber",
                    "Machine wash up to 20 cycles",
                    "5 HAI zone-coded colors",
                    "50 ct dispensing box · 8/case",
                    "USA final assembly",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/marketplace/product/sterilemate-disposable-microfiber-rags-12x12"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-5 py-3 shadow-lg shadow-amber-500/25 transition-colors"
                    data-testid="sterilemate-microfiber-cta-view"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    View Product &amp; Reserve
                  </Link>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/sterilemate_microfiber_12x12_dieline_${microfiberColor}.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/25 text-white font-bold px-5 py-3 transition-colors"
                    data-testid="sterilemate-microfiber-cta-dieline"
                  >
                    <FileText className="w-4 h-4" />
                    Download {microfiberColor[0].toUpperCase() + microfiberColor.slice(1)} Dieline (PDF)
                  </a>
                </div>
                <p className="text-xs text-white/60 mt-3">
                  Manufacturer artwork · dimensions · cut &amp; fold specs — for your box supplier
                </p>
              </div>
            </div>

            {/* Section divider */}
            <div className="border-t border-white/10 bg-slate-950/40 px-8 py-4 text-center text-sm text-white/70">
              <span className="uppercase tracking-widest text-[11px] font-bold text-cyan-300">
                Shop Our Current Microfiber Products
              </span>
              <span className="mx-3 text-white/30">·</span>
              <span>Mops · Pads · Frames · Buckets — scroll to see all in-stock SKUs</span>
            </div>
          </section>
        )}

        {/* Sterile Mate Microfiber launch teaser — home-page ("all" categories) awareness card */}
        {selectedCategory === "all" && !searchQuery && (
          <Link
            to="/marketplace?category=evs-janitorial&sub=Microfiber"
            data-testid="sterilemate-microfiber-home-teaser"
            className="group relative block mb-8 overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-xl hover:shadow-2xl transition-all"
          >
            <div className="grid md:grid-cols-3 gap-0 items-stretch">
              <div className="relative bg-black/30 flex items-center justify-center p-4 md:p-3">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/static/microfiber-rags/hero_branded_blue.png`}
                  alt="Sterile Mate Microfiber Cleaning Rags"
                  className="w-full h-auto max-h-52 object-contain rounded-lg"
                />
              </div>
              <div className="md:col-span-2 p-6 md:p-8 flex flex-col justify-center">
                <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-amber-400 mb-2">
                  New · Sterile Mate Own-Brand · Coming Q2 2026
                </p>
                <h3 className="text-xl md:text-2xl font-black leading-tight mb-2">
                  Sterile Mate<sup>®</sup> Microfiber Cleaning Rags — 12&quot;×12&quot;
                </h3>
                <p className="text-sm md:text-base text-white/80 mb-4">
                  Machine washable up to 20 cycles · 5 HAI zone-coded colors ·
                  Tear-off dispensing box · 220 GSM precision cut. Preview the
                  artwork, download the dieline, and reserve your stock.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-2 rounded-full bg-amber-500 group-hover:bg-amber-400 text-slate-900 font-bold px-4 py-2 text-sm transition-colors">
                    See the Microfiber Launch
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                  <span className="text-xs text-white/60">→ full page + downloads inside Marketplace / Microfiber</span>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Hemodialysis Command Center banner — routes buyers to the /dialysis small business */}
        {selectedCategory === "hemodialysis" && (
          <Link
            to="/dialysis"
            data-testid="hemodialysis-command-center-banner"
            className="group relative block mb-8 overflow-hidden rounded-2xl bg-gradient-to-r from-[#0a1a35] via-[#0b2447] to-teal-700 text-white p-6 md:p-8 shadow-xl hover:shadow-2xl transition-all"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(45,212,191,0.25),transparent_60%)] pointer-events-none" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="max-w-2xl">
                <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-teal-300 mb-2">
                  Dialysis Command Center · New
                </p>
                <h3 className="text-xl md:text-2xl font-black leading-tight mb-1">
                  Full-stack dialysis: water, concentrates, equipment &amp; compliance — one small business.
                </h3>
                <p className="text-sm md:text-base text-white/80">
                  Positioning vs Fresenius / DaVita / Rockwell, CMS ESRD V-tag coverage, and Gulf-region dispatch.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 shrink-0 rounded-full bg-teal-400 hover:bg-teal-300 text-slate-900 font-bold px-5 py-3 text-sm shadow-lg shadow-teal-500/30 transition-colors">
                Visit Command Center
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
          </Link>
        )}

        {/* Featured Products — within the active filter when a sub-chip is on */}
        {!searchQuery
          && featuredProducts.length > 0 && (
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
              <h2 className="text-2xl font-bold text-slate-800">Featured Products</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  onViewDetails={goToProductPage}
                />
              ))}
            </div>
          </section>
        )}

        {/* All Products */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-800">
              {selectedCategory === "all" ? "All Products" : CATEGORIES.find(c => c.id === selectedCategory)?.name}
            </h2>
            <p className="text-slate-500">{filteredProducts.length} products</p>
          </div>
          
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No products found</h3>
              <p className="text-slate-500">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  onViewDetails={goToProductPage}
                />
              ))}
            </div>
          )}
        </section>

        {/* Green Klean Section */}
        {(selectedCategory === "all" || selectedCategory === "green-klean") && (
          <section className="mb-12">
            <div className="bg-gradient-to-r from-emerald-700 to-emerald-600 rounded-2xl p-8 mb-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <img 
                    src="https://gogreenklean.com/wp-content/uploads/2023/02/Green-Klean_Final-Logo_No-Tag-768x161.png" 
                    alt="Green Klean" 
                    className="h-16 object-contain bg-white rounded-lg px-4 py-2"
                  />
                  <div className="text-white">
                    <h2 className="text-2xl font-bold">Green Klean® Products</h2>
                    <p className="text-emerald-100">We think differently about clean.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="secondary" 
                    className="bg-white text-emerald-700 hover:bg-emerald-50"
                    asChild
                  >
                    <a href="https://sterilemate-ims-hart.preview.emergentagent.com/api/pdf-brochures/green-klean-price-guide/preview" target="_blank" rel="noopener noreferrer">
                      <Eye className="w-4 h-4 mr-2" />
                      View Price Guide
                    </a>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-white text-white hover:bg-white/10"
                    asChild
                  >
                    <a href="https://sterilemate-ims-hart.preview.emergentagent.com/api/pdf-brochures/green-klean-price-guide/download" target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </a>
                  </Button>
                </div>
              </div>
              
              {/* Green Klean Subcategory Filter */}
              <div className="flex flex-wrap gap-2 mt-6">
                <button
                  onClick={() => setGreenKleanSubcategory("all")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    greenKleanSubcategory === "all"
                      ? 'bg-white text-emerald-700'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  All ({greenKleanProducts.length})
                </button>
                {greenKleanSubcategories.map(subcat => (
                  <button
                    key={subcat}
                    onClick={() => setGreenKleanSubcategory(subcat)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      greenKleanSubcategory === subcat
                        ? 'bg-white text-emerald-700'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    {subcat} ({greenKleanProducts.filter(p => p.subcategory === subcat).length})
                  </button>
                ))}
              </div>
            </div>
            
            {/* Green Klean Product Grid */}
            {greenKleanLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                <span className="ml-3 text-slate-600">Loading Green Klean products...</span>
              </div>
            ) : filteredGreenKleanProducts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <Leaf className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-600 mb-2">No products found</h3>
                <p className="text-slate-500">Try selecting a different category</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredGreenKleanProducts.map(product => (
                  <GreenKleanProductCard 
                    key={product.id} 
                    product={product} 
                    onViewDetails={setSelectedGreenKleanProduct}
                  />
                ))}
              </div>
            )}
            
            {/* Green Klean Features */}
            <div className="mt-8 grid md:grid-cols-4 gap-4">
              {[
                { icon: Award, title: "Green Diamond Warranty", desc: "No-excuse quality guarantee" },
                { icon: Shield, title: "OEM Compatible", desc: "Exceeds original specs" },
                { icon: Leaf, title: "Eco-Responsible", desc: "Sustainable solutions" },
                { icon: Truck, title: "Same Day Shipping", desc: "Fast delivery available" }
              ].map((feature, i) => (
                <div key={i} className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                  <feature.icon className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <h4 className="font-bold text-slate-800">{feature.title}</h4>
                  <p className="text-sm text-slate-600">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Authorized Distributor Banner */}
        <section className="mt-12 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold mb-2">Authorized Distributor</h3>
              <p className="text-slate-300">
                Sterile Mate is an authorized distributor for leading healthcare brands including 
                Sterile Mate, Filmop USA, Minuteman, MultiClean, Microfiber USA, and ABCO Products.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-white/10 text-white border-white/20">Green Klean</Badge>
              <Badge className="bg-white/10 text-white border-white/20">Sterile Mate</Badge>
              <Badge className="bg-white/10 text-white border-white/20">Filmop USA</Badge>
              <Badge className="bg-white/10 text-white border-white/20">Minuteman</Badge>
              <Badge className="bg-white/10 text-white border-white/20">MultiClean</Badge>
              <Badge className="bg-white/10 text-white border-white/20">ABCO</Badge>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="mt-12 bg-emerald-600 rounded-2xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Need Help Choosing?</h3>
          <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
            Our team can help you select the right products for your facility. 
            Request a consultation or schedule a product demo.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50" asChild>
              <a href="tel:8009883892">
                <Phone className="w-5 h-5 mr-2" />
                800.988.3892
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" asChild>
              <a href="mailto:george.broughton@sterilemate.com">
                <Mail className="w-5 h-5 mr-2" />
                Request Demo
              </a>
            </Button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Sterile Mate LLC. All rights reserved.
          </p>
          <p className="text-slate-500 text-xs mt-2">
            Houston, Texas | Serving Healthcare Facilities Nationwide
          </p>
        </div>
      </footer>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal 
          product={selectedProduct} 
          onClose={() => setSelectedProduct(null)} 
        />
      )}
      
      {/* Green Klean Product Detail Modal */}
      {selectedGreenKleanProduct && (
        <GreenKleanDetailModal 
          product={selectedGreenKleanProduct} 
          onClose={() => setSelectedGreenKleanProduct(null)} 
        />
      )}
    </div>
  );
}
